(() => {
  // src/store.js
  var state = {
    status: "empty",
    // empty | uploading | uploaded
    mode: "single",
    // single | multi
    // rows carry NO price — unit/total live in state.pricing.rows (pricing is
    // the sole writer there; sizing renders its cells from it, matched by
    // fileId + row index). File dims: widthIn/heightIn canonical; the ported
    // pipeline's inW/inH are stamped as aliases at record creation.
    files: [],
    // [{ id, name, type, s3Url, imgixUrl, widthIn, heightIn, rows: [{ w, h, qty, variantId, mode }], designNote }]
    activeFileId: null,
    // which file the page preview/tools show; null => files[0]
    // LIVE TAB SEMANTICS (Owen 2026-07-07, ported from bySize_popular.js):
    // custom and popular rows are SEPARATE persistent lists (row.mode tags
    // them; missing mode reads as 'custom'). sizeTab picks which list renders,
    // prices, previews and CHECKS OUT — active-tab-only at add-to-cart
    // (deliberate delta: live's edited-both-tabs cross-add quirk NOT ported).
    sizeTab: "custom",
    // custom | popular — written by sizing on tab switch
    precut: false,
    designNotes: "",
    cartQty: 0,
    // cumulative DTF/UV qty already in cart (tier input); seeded from cfg.cartQty at boot (main.js)
    family: "dtf",
    // dtf | uv | sticker | precut | puff | cling — set once at boot from cfg
    options: {},
    // family option selections, e.g. { shape:'circle', finish:'gloss' } (empty for dtf)
    pricing: { model: "cumulative", rows: [], total: 0, tier: null }
    // written ONLY by pricing; rows: [{fileId,w,h,qty,unit,total}]
  };
  var listeners = /* @__PURE__ */ new Set();
  function getState() {
    return state;
  }
  function update(patch) {
    Object.assign(state, patch);
    emit(patch);
  }
  function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }
  function emit(patch) {
    for (const fn of listeners) fn(state, patch);
  }
  function reset() {
    update({
      status: "empty",
      mode: "single",
      files: [],
      activeFileId: null,
      precut: false,
      designNotes: "",
      options: {},
      pricing: { model: state.pricing.model, rows: [], total: 0, tier: null }
    });
    announce("reset", {});
  }
  function announce(name, detail) {
    document.dispatchEvent(new CustomEvent(`bsv4:${name}`, { detail, bubbles: false }));
  }

  // src/config.js
  function parseImgixMatrix(dsl) {
    const out = {};
    if (!dsl || typeof dsl !== "string") return out;
    for (const line of dsl.split("\n")) {
      const ci = line.indexOf(":");
      if (ci < 1) continue;
      const ext = line.slice(0, ci).trim().toLowerCase();
      if (!ext) continue;
      const entry = {};
      for (const seg of line.slice(ci + 1).split(";")) {
        const ei = seg.indexOf("=");
        if (ei < 1) continue;
        const k = seg.slice(0, ei).trim();
        let v = seg.slice(ei + 1).trim();
        if (v === "true") v = true;
        else if (v === "false") v = false;
        entry[k] = v;
      }
      if (Object.keys(entry).length) out[ext] = entry;
    }
    return out;
  }
  function parsePopularCaps(dsl) {
    const out = [];
    if (!dsl || typeof dsl !== "string") return out;
    for (const line of dsl.split("\n")) {
      const ci = line.indexOf(":");
      if (ci < 1) continue;
      const size = line.slice(0, ci).trim().toLowerCase();
      const sm = size.match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/);
      if (!sm) continue;
      const caps = { w: parseFloat(sm[1]), h: parseFloat(sm[2]) };
      for (const seg of line.slice(ci + 1).split(",")) {
        const ei = seg.indexOf("=");
        if (ei < 1) continue;
        const k = seg.slice(0, ei).trim();
        const v = parseFloat(seg.slice(ei + 1));
        if (k === "max_w" && !isNaN(v)) caps.maxW = v;
        if (k === "max_h" && !isNaN(v)) caps.maxH = v;
      }
      out.push(caps);
    }
    return out;
  }
  function readConfig() {
    const el = document.getElementById("bsv4-config");
    const cfg7 = el ? JSON.parse(el.textContent) : {};
    if (cfg7.preview) cfg7.preview.imgix = parseImgixMatrix(cfg7.preview.imgixMatrix);
    if (Array.isArray(cfg7.sizing)) {
      for (const s of cfg7.sizing) s.popular = parsePopularCaps(s.popularCaps);
    }
    if (!cfg7.imgixParams || !Object.keys(cfg7.imgixParams).length) {
      cfg7.imgixParams = cfg7.preview ? cfg7.preview.imgix : {};
    }
    return cfg7;
  }

  // src/ported/pipeline.js
  var API_URL = "https://pan32vgek6.execute-api.us-east-1.amazonaws.com";
  var IMGIX_HOST = "dtf-store.imgix.net";
  var S3_HOST = "dtf-stores.s3.amazonaws.com";
  function buildUploadName(fileName) {
    const nameOnly = fileName.split(".")[0].toLowerCase().replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
    const extension = fileName.split(".").pop();
    return `${nameOnly}____image_${Date.now()}_${Math.floor(Math.random() * 1e3)}.${extension}`;
  }
  async function getPresignedUploadUrl(fileName, fileType, fileSize) {
    const ext = (fileName.split(".").pop() || "").toLowerCase();
    const res = await fetch(`${API_URL}/upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ext, type: fileType || "application/octet-stream", size: fileSize || 0 })
    });
    if (!res.ok) throw new Error(`presign failed (${res.status})`);
    const p = await res.json();
    if (!p || !p.url) throw new Error("presign unavailable");
    return { url: p.url, sourceUrl: `https://${S3_HOST}/${p.key || ""}`, key: p.key, path: (p.path || "").replace(/^\//, "") };
  }
  function putToS3(presignedUrl, file, onProgress, onXhr) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error(`S3 PUT ${xhr.status}`));
      xhr.onerror = () => reject(new Error("S3 PUT network error"));
      xhr.onabort = () => reject(Object.assign(new Error("upload cancelled"), { name: "AbortError" }));
      if (onXhr) onXhr(xhr);
      xhr.send(file);
    });
  }
  var crcTable;
  function crc32Buf(buf) {
    if (!crcTable) {
      crcTable = new Int32Array(256);
      for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 3988292384 ^ c >>> 1 : c >>> 1;
        crcTable[n] = c;
      }
    }
    let crc = -1;
    for (let i = 0; i < buf.length; i++) crc = crc >>> 8 ^ crcTable[(crc ^ buf[i]) & 255];
    return crc ^ -1;
  }
  async function crc32Hex(file) {
    const buf = new Uint8Array(await file.arrayBuffer());
    return (crc32Buf(buf) >>> 0).toString(16);
  }
  async function calculateInches({ s3Key, url }) {
    const base = url || `https://${IMGIX_HOST}/${s3Key}`;
    const sep = base.includes("?") ? "&" : "?";
    const res = await fetch(`${base}${sep}fm=json`);
    if (!res.ok) throw new Error(`calculateInches failed (${res.status})`);
    const j = await res.json();
    const pxW = j.PixelWidth || 0;
    const pxH = j.PixelHeight || 0;
    if (!pxW || !pxH) throw new Error("calculateInches: no dimensions");
    const dpi = j.DPIWidth || 72;
    return { success: true, data: { dimensions: { width: pxW / dpi, height: pxH / dpi } } };
  }
  function saveFileToUser({ customerId, productId, fileRecord }) {
    // No NOD designs API yet — record into the per-browser history so the
    // Previous Designs grid lists this upload (nod-local-designs.js).
    try {
      if (window.NODLocalDesigns && fileRecord && (fileRecord.imgixUrl || fileRecord.s3Url)) {
        window.NODLocalDesigns.save({
          file: fileRecord.imgixUrl || fileRecord.s3Url,
          s3: fileRecord.s3Url || "",
          name: fileRecord.name || ""
        });
      }
    } catch (e) {
    }
    return;
  }
  function measureImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = () => reject(new Error(`image failed to load: ${url}`));
      img.src = url;
    });
  }
  function stripSizeParams(params) {
    return (params || "").split("&").filter((p) => p && !/^[wh]=/.test(p)).join("&");
  }
  async function resolveDimensions(fileRecord, previewParams) {
    const ext = fileRecord.ext;
    let pxW;
    let pxH;
    if (ext === "eps" || ext === "ai") {
      const res = await calculateInches({ url: fileRecord.imgixUrl });
      if (res && res.success) {
        pxW = res.data.dimensions.width * 300;
        pxH = res.data.dimensions.height * 300;
      }
    } else if (ext === "pdf" || ext === "psd") {
      const res = await calculateInches({ url: fileRecord.imgixUrl });
      if (res && res.success) {
        pxW = res.data.dimensions.width * 300;
        pxH = res.data.dimensions.height * 300;
      }
    } else {
      const measureUrl = `${fileRecord.imgixUrl}?${stripSizeParams(previewParams)}`;
      const m = await measureImage(measureUrl);
      pxW = m.w;
      pxH = m.h;
      if (ext === "svg") {
        pxW *= 4.168067226890756;
        pxH *= 4.168067226890756;
      }
    }
    if (!pxW || !pxH) {
      pxW = 300;
      pxH = 300;
    }
    return {
      pxW: Math.round(pxW),
      pxH: Math.round(pxH),
      inW: (pxW / 300).toFixed(2),
      inH: (pxH / 300).toFixed(2)
    };
  }

  // src/ported/bgr.js
  var BGR_MODELS = [
    { key: "imgix", label: "Imgix", modelParam: null },
    { key: "ideogram", label: "Ideogram", modelParam: null },
    { key: "pixelcut", label: "PixelCut", modelParam: null }
  ];
  function candidateUrl(bareImgixUrl, model2) {
    const base = `${bareImgixUrl.split("?")[0]}?trim=colorUnlessAlpha&fm=png&w=2000&bg-remove=true`;
    return model2.modelParam ? `${base}&bg-remove-model=${model2.modelParam}` : base;
  }
  var BGR_VERSION_PROP_LABELS = {
    imgix: "_Imgix Version",
    ideogram: "_Ideogram Version",
    pixelcut: "_Pixel Version"
  };
  function applyBgrProps(properties, file) {
    if (!file.bgRemove || !file.bgrVersions || !file.bgrModel) return properties;
    const chosen = file.bgrVersions[file.bgrModel];
    if (!chosen) return properties;
    properties["Upload (Vector Files Preferred)"] = chosen;
    properties["_cartImg"] = `${chosen}&auto=compress&q=50&h=100`;
    properties["_BG Removal Service"] = BGR_MODELS.find((m) => m.key === file.bgrModel)?.label || file.bgrModel;
    for (const m of BGR_MODELS) {
      if (m.key === file.bgrModel) continue;
      const url = file.bgrVersions[m.key];
      if (url) properties[BGR_VERSION_PROP_LABELS[m.key]] = url;
    }
    return properties;
  }

  // src/ported/cart-payload.js
  function isValidOrderRef(v) {
    if (v === null || v === void 0) return false;
    let s = String(v).trim();
    if (!s) return false;
    s = s.replace(/^previously\s+on\s+order\s*#?\s*/i, "").trim();
    if (!s) return false;
    return /^[A-Z]{2,4}-?\d{4,}$/i.test(s);
  }
  function parseOrderString(imgIxURL, s3URL, order, selectedOptions = "") {
    const result = { imgIx: false, imgIx_params: "", s3: false, s3_params: "" };
    selectedOptions = (selectedOptions || "").replace(/^[?&]+/, "");
    const normalizePart = (part) => part.replace(/^(\[(?:imgIx|s3|both_imgix|both_s3)\])([^?].+)/i, "$1?$2");
    const buildUrl = (baseURL, rawParams, appendSelected) => {
      let url = baseURL;
      if (rawParams) url += rawParams;
      if (appendSelected && selectedOptions) {
        url += (rawParams.includes("?") ? "&" : "?") + selectedOptions;
      }
      return url;
    };
    (order || "").split("&&").forEach((rawPart) => {
      const part = normalizePart(rawPart.trim());
      const match = part.match(/\[(imgIx|s3|both_imgix|both_s3)\](\?[^\s]*)?/i);
      if (!match) return;
      const key = match[1].toLowerCase();
      const rawParams = (match[2] || "").trim();
      if (key === "imgix") {
        result.imgIx = true;
        result.imgIx_params = buildUrl(imgIxURL, rawParams, true);
      } else if (key === "s3") {
        result.s3 = true;
        result.s3_params = buildUrl(s3URL, rawParams, false);
      } else if (key === "both_imgix") {
        result.imgIx = true;
        result.s3 = true;
        result.imgIx_params = buildUrl(imgIxURL, rawParams, true);
        result.s3_params = buildUrl(imgIxURL, rawParams, true);
      } else if (key === "both_s3") {
        result.imgIx = true;
        result.s3 = true;
        result.imgIx_params = buildUrl(s3URL, rawParams, false);
        result.s3_params = buildUrl(s3URL, rawParams, false);
      }
    });
    return result;
  }
  function toggleParams(file) {
    if (!file.bgRemove && !file.superRes) return null;
    let p = "";
    if (file.bgRemove) p += "&bg-remove=true";
    if (file.superRes) p += "&upscale=true";
    return p;
  }
  var attribution = { designType: false, orderName: "", photoId: "" };
  function setAttribution({ designType, orderName, photoId }) {
    if (designType !== void 0) attribution.designType = designType;
    if (orderName !== void 0) attribution.orderName = orderName;
    if (photoId !== void 0) attribution.photoId = photoId;
  }
  function buildItem(file, row, { designNote, precut, popupSource }) {
    const properties = {};
    const options = toggleParams(file) || file.currentOptions || "";
    const orderProperty = parseOrderString(file.imgixUrl, file.s3Url, file.params.order, options);
    if (orderProperty.imgIx) {
      properties["Upload (Vector Files Preferred)"] = orderProperty.imgIx_params || "";
      if (attribution.designType && isValidOrderRef(attribution.photoId)) {
        properties["design re-used"] = `${attribution.photoId}`;
        attribution.designType = false;
        attribution.orderName = "";
      }
      if (attribution.designType === 1 && isValidOrderRef(attribution.orderName)) {
        properties["design re-used"] = `previously on order # ${attribution.orderName}`;
        attribution.designType = false;
        attribution.orderName = "";
      }
    }
    if (orderProperty.s3) {
      if (file.isReadyToPress === "Yes") {
        properties["Upload (Vector Files Preferred)"] = orderProperty.s3_params || "";
      } else if (file.isAIEdit && file.originalUploadReference) {
        properties["_Original Image"] = file.originalUploadReference;
      } else {
        properties["_Original Image"] = orderProperty.s3_params || "";
      }
    }
    properties["_cartImg"] = `${file.imgixUrl}?${file.params.cart}${toggleParams(file) || file.currentOptions || ""}`;
    properties["Remove Background"] = file.bgRemove ? "Yes" : "No";
    properties["Super Resolution"] = file.superRes ? "Yes" : "No";
    if (designNote) properties["Design Notes"] = designNote;
    if (row.dpiWarn) properties["DPI Warning"] = "Yes";
    if (row.mode === "popular") properties["_pop size"] = "Yes";
    if (precut) properties["Precut"] = "Yes";
    properties["_width"] = String(row.w);
    properties["_height"] = String(row.h);
    if (file.isReadyToPress === "Yes") properties["_Ready to Press"] = "Yes";
    if (file.isDesignStudio === "Yes") properties["_Design Studio"] = "Yes";
    if (file.isAIImage) properties["AI Created"] = "Yes";
    if (file.isAIEdit) properties["AI Edit"] = "Yes";
    if (popupSource) properties["_popup_source"] = "dtf_popup";
    applyBgrProps(properties, file);
    return { id: row.variantId, quantity: row.qty, properties };
  }

  // src/ported/ai-edit.js
  async function nanoBananaEdit(imageUrl, promptText, falKey, signal) {
    const resp = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageUrl, prompt: promptText }),
      signal
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(`AI edit request failed (${resp.status}): ${txt.slice(0, 200)}`);
    }
    const data = await resp.json();
    if (data && data.url) return data.url;
    const path = data && (data.path || data.key);
    if (!path) throw new Error("AI edit response missing path");
    return `https://${IMGIX_HOST}/${path}`;
  }

  // src/uploader.js
  var EXT_MIME = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    ai: "application/postscript",
    eps: "application/postscript",
    pdf: "application/pdf",
    tif: "image/tiff",
    tiff: "image/tiff",
    svg: "image/svg+xml"
  };
  var MAX_BYTES = 5368709120;
  var DEFAULT_MAX_FILES = 25;
  var cfg;
  var els;
  var seq = 0;
  var announcingStore = false;
  var tabEventsWired = false;
  var aiShimsInstalled = false;
  var addAnotherGate = null;
  function setAddAnotherGate(fn) {
    addAnotherGate = fn;
  }
  function installAiHandoffShims() {
    if (aiShimsInstalled) return;
    aiShimsInstalled = true;
    const w = window;
    const toEntry = (d) => {
      const url = String(d && (d.fileURL || d.url) || "").split("?")[0];
      if (!url) return null;
      return {
        url,
        name: d && d.name || url.split("/").pop(),
        isAIImage: d && d.isAIImage || void 0,
        isAIEdit: d && d.isAIEdit || void 0,
        isDesignStudio: d && d.isDesignStudio || void 0,
        isReadyToPress: d && d.isReadyToPress || void 0
      };
    };
    if (typeof w.getPresignedUploadUrl !== "function") {
      w.getPresignedUploadUrl = getPresignedUploadUrl;
    }
    if (typeof w.nanoBananaEditRequest !== "function" && cfg && cfg.falKey) {
      w.nanoBananaEditRequest = (imageUrl, promptText) => nanoBananaEdit(imageUrl, promptText, cfg.falKey);
    }
    if (!w.__dtfutFetchPatched && typeof w.fetch === "function") {
      w.__dtfutFetchPatched = true;
      const origFetch = w.fetch;
      w.fetch = function(url, options) {
        try {
          const urlStr = typeof url === "string" ? url : url && url.url || "";
          if (urlStr.indexOf("getGenerativeAiUploads.php") !== -1) {
            return origFetch.apply(this, arguments).then(
              (resp) => resp.clone().json().then((data) => {
                const capped = Array.isArray(data) ? data.slice(0, 3) : [];
                if (capped.length > 0) w.__dtfutSkipNewAIGeneration = true;
                return new Response(JSON.stringify(capped), {
                  status: resp.status,
                  headers: { "Content-Type": "application/json" }
                });
              }).catch(() => resp)
            );
          }
          if (w.__dtfutSkipNewAIGeneration && urlStr.indexOf("postBatchGenerativeAiUpload.php") !== -1) {
            w.__dtfutSkipNewAIGeneration = false;
            return Promise.resolve(
              new Response("[]", { status: 200, headers: { "Content-Type": "application/json" } })
            );
          }
        } catch (e) {
        }
        return origFetch.apply(this, arguments);
      };
    }
    if (typeof w.createFileObjectFromUrl !== "function") {
      w.createFileObjectFromUrl = (url) => ({
        name: String(url || "").split("?")[0].split("/").pop() || "ai-image.png",
        fileURL: url
      });
    }
    if (typeof w.singleFileManageByURL !== "function") {
      w.singleFileManageByURL = (detail) => {
        const e = toEntry(detail);
        if (!e) return;
        if (addAnotherGate && addAnotherGate({ entries: [e] })) return;
        intakeFromUrls([e]);
      };
    }
    if (typeof w.multiFilesManageByURL !== "function") {
      w.multiFilesManageByURL = (files) => {
        const entries = (files || []).map(toEntry).filter(Boolean);
        if (!entries.length) return;
        if (addAnotherGate && addAnotherGate({ entries })) return;
        intakeFromUrls(entries);
      };
    }
    if (typeof w.uploadFile !== "function") {
      w.uploadFile = (file) => {
        if (!file) return;
        queueMicrotask(() => {
          if (w.isAIImage) {
            file.isAIImage = true;
            w.isAIImage = false;
          }
          if (addAnotherGate && addAnotherGate({ files: [file] })) return;
          intake([file]);
        });
      };
    }
    if (typeof w.manageFiles !== "function") {
      w.manageFiles = (files) => {
        if (!files || !files.length) return;
        if (addAnotherGate && addAnotherGate({ files })) return;
        intake(files);
      };
    }
  }
  function setSource(src) {
    if (!els || !els.box) return;
    for (const pill of els.box.querySelectorAll("[data-bsv4-source]")) {
      pill.classList.toggle("is-active", pill.dataset.bsv4Source === src);
    }
    const titleEl = els.box.querySelector("[data-bsv4-title]");
    const activePill = els.box.querySelector(`[data-bsv4-source="${src}"]`);
    if (titleEl && activePill && activePill.dataset.bsv4TitleText) {
      titleEl.textContent = activePill.dataset.bsv4TitleText;
    }
    const panelSrc = src === "studio" ? "upload" : src;
    for (const panel of els.box.querySelectorAll("[data-bsv4-panel]")) {
      panel.hidden = panel.dataset.bsv4Panel !== panelSrc;
    }
    announce("source-changed", { source: src });
    if (src === "ai") {
      document.dispatchEvent(
        new CustomEvent("dtfut:tab-change", { detail: { name: "ai" }, bubbles: true })
      );
    }
  }
  function initUploader(root3, config) {
    cfg = config;
    const box = root3.querySelector('[data-bsv4-block="upload_box"]');
    if (!box || !cfg.uploadBox) return;
    if (box.dataset.bsv4UploaderBound === "1") return;
    box.dataset.bsv4UploaderBound = "1";
    els = {
      root: root3,
      box,
      zone: box.querySelector("[data-bsv4-dropzone]"),
      input: box.querySelector("[data-bsv4-file-input]")
    };
    if (!els.zone || !els.input) return;
    installCancelHook();
    const entries = cfg.uploadBox.entries || {};
    for (const [source, on] of [
      ["previous", entries.previousDesigns],
      ["ai", entries.aiCreate],
      ["studio", entries.designStudio]
    ]) {
      const pill = box.querySelector(`[data-bsv4-source="${source}"]`);
      if (pill) pill.hidden = on === false;
    }
    els.input.accept = allowedExts().map((e) => `.${e}`).join(",");
    els.input.multiple = resolveIntakeCap(cfg) > 1;
    els.zone.addEventListener("click", () => els.input.click());
    box.addEventListener("click", (e) => {
      const pill = e.target.closest("[data-bsv4-source]");
      if (!pill) return;
      e.preventDefault();
      setSource(pill.dataset.bsv4Source);
    });
    els.zone.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        els.input.click();
      }
    });
    els.zone.addEventListener("dragover", (e) => {
      e.preventDefault();
      els.zone.classList.add("is-dragover");
    });
    els.zone.addEventListener("dragleave", () => els.zone.classList.remove("is-dragover"));
    els.zone.addEventListener("drop", (e) => {
      e.preventDefault();
      els.zone.classList.remove("is-dragover");
      if (e.dataTransfer && e.dataTransfer.files.length) intake(e.dataTransfer.files);
    });
    els.input.addEventListener("change", () => {
      if (els.input.files.length) intake(els.input.files);
    });
    initPageDrop();
    box.addEventListener("paste", (e) => {
      const pasted = e.clipboardData && e.clipboardData.files;
      if (pasted && pasted.length) {
        e.preventDefault();
        intake(pasted);
      }
    });
    const addZone = root3.querySelector("[data-bsv4-add-zone]");
    if (addZone && addZone.dataset.bsv4UploaderBound !== "1") {
      addZone.dataset.bsv4UploaderBound = "1";
      addZone.addEventListener("click", () => els.input.click());
      addZone.addEventListener("dragover", (e) => {
        e.preventDefault();
        addZone.classList.add("is-dragover");
      });
      addZone.addEventListener("dragleave", () => addZone.classList.remove("is-dragover"));
      addZone.addEventListener("drop", (e) => {
        e.preventDefault();
        addZone.classList.remove("is-dragover");
        if (e.dataTransfer && e.dataTransfer.files.length) intake(e.dataTransfer.files);
      });
    }
    installAiHandoffShims();
    if (!tabEventsWired) {
      tabEventsWired = true;
      document.addEventListener("bsv4:files-received", () => setSource("upload"));
    }
    if (!announcingStore) {
      announcingStore = true;
      let known = getState().files.map((f) => f.id);
      subscribe((state2, patch) => {
        if (!("files" in patch)) return;
        const ids = state2.files.map((f) => f.id);
        for (const f of state2.files) {
          if (!known.includes(f.id)) announce("file-added", { id: f.id, name: f.name });
        }
        for (const id of known) {
          if (!ids.includes(id)) announce("file-removed", { id });
        }
        known = ids;
      });
    }
  }
  var pageDropWired = false;
  var pageDropActive = false;
  function isFileDrag(e) {
    const types = e.dataTransfer && e.dataTransfer.types;
    if (!types) return false;
    for (const t of types) if (t === "Files") return true;
    return false;
  }
  function showDropScreen() {
    if (document.querySelector(".bsv4-dropscreen")) return;
    const el = document.createElement("div");
    el.className = "bsv4-dropscreen";
    const plural = els && els.input && els.input.multiple;
    el.innerHTML = `<div class="bsv4-dropscreen__inner"><div class="bsv4-dropscreen__glyph" aria-hidden="true"></div><div class="bsv4-dropscreen__title">Drop your file${plural ? "(s)" : ""}</div><div class="bsv4-dropscreen__sub">anywhere to upload</div></div>`;
    document.body.appendChild(el);
  }
  function clearDropScreen() {
    const el = document.querySelector(".bsv4-dropscreen");
    if (el) el.remove();
    pageDropActive = false;
  }
  function initPageDrop() {
    if (pageDropWired) return;
    pageDropWired = true;
    document.addEventListener(
      "dragover",
      (e) => {
        if (getState().status !== "empty") return;
        if (!isFileDrag(e)) return;
        e.preventDefault();
        if (!pageDropActive) {
          pageDropActive = true;
          showDropScreen();
        }
      },
      true
    );
    document.addEventListener(
      "dragleave",
      (e) => {
        if (!e.relatedTarget) clearDropScreen();
      },
      true
    );
    document.addEventListener("dragend", clearDropScreen, true);
    document.addEventListener(
      "drop",
      (e) => {
        if (pageDropActive) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const files = e.dataTransfer && e.dataTransfer.files;
          if (files && files.length) intake(files);
        }
        clearDropScreen();
      },
      true
    );
  }
  function resolveIntakeCap(config) {
    const c = config || {};
    const entryOn = c.uploadBox && c.uploadBox.entries ? c.uploadBox.entries.multiupload !== false : true;
    if (!c.modal || !entryOn) return 1;
    const n = Number(c.modal.maxFiles);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_MAX_FILES;
  }
  function fileIntakeError(file, exts) {
    const ext = String((file && file.name || "").split(".").pop() || "").toLowerCase();
    const okExt = exts.includes(ext);
    const okMime = exts.some((e) => {
      const mime = EXT_MIME[e];
      return mime && file.type && file.type.indexOf(mime) !== -1;
    });
    if (!okExt && !okMime) {
      return exts.length ? `File type not supported. Allowed: ${exts.map((e) => e.toUpperCase()).join(", ")}` : "File type not supported.";
    }
    if (file.size > MAX_BYTES) return "File must be 5 GB or less";
    return null;
  }
  function allowedExts() {
    const list = cfg && cfg.uploadBox && Array.isArray(cfg.uploadBox.fileTypes) ? cfg.uploadBox.fileTypes : [];
    return list.map((e) => String(e).toLowerCase().replace(/^\./, "")).filter(Boolean);
  }
  async function intake(fileList) {
    if (!els) return;
    if (getState().status === "uploading" && getState().mode !== "multi") return;
    let files = Array.from(fileList);
    els.input.value = "";
    const cap = resolveIntakeCap(cfg);
    const existing = getState().files.length;
    if (cap === 1 && existing + files.length > 1) {
      showUploadErrorCard(
        activeUploadZone(),
        existing ? "This product takes one design at a time \u2014 remove the current design first." : "This product takes one design at a time \u2014 only the first file was kept."
      );
      files = files.slice(0, Math.max(0, 1 - existing));
      if (!files.length) return;
    } else if (existing + files.length > cap) {
      const skipped = existing + files.length - cap;
      showUploadErrorCard(activeUploadZone(), `Maximum ${cap} designs per order \u2014 ${skipped} file(s) skipped.`);
      files = files.slice(0, cap - existing);
      if (!files.length) return;
    }
    const exts = allowedExts();
    const accepted = [];
    let typeRejected = false;
    for (const file of files) {
      const err = fileIntakeError(file, exts);
      if (err) {
        typeRejected = true;
        announce("file-rejected", { name: file.name, error: err });
        continue;
      }
      accepted.push(file);
    }
    if (typeRejected) showUploadErrorCard(activeUploadZone(), fileTypesSentence());
    if (!accepted.length) return;
    update({
      status: "uploading",
      mode: getState().mode === "multi" || accepted.length > 1 || getState().files.length ? "multi" : "single"
    });
    announce("files-received", { count: accepted.length });
    ldlReset();
    activeBatches++;
    try {
      await Promise.all(accepted.map((file) => uploadOne(file)));
    } finally {
      activeBatches--;
    }
    settleBatch();
  }
  function settleBatch() {
    if (activeBatches > 0) return;
    const state2 = getState();
    if (state2.files.length) {
      update({ status: "uploaded" });
      announce("upload-complete", { count: state2.files.length });
      if (state2.mode === "multi") ldlDone();
      scrollToTop();
    } else {
      ldlDone();
      update({ status: "empty", mode: "single" });
    }
  }
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  async function uploadBlob(file, onProgress, onXhr) {
    const uploadName = buildUploadName(file.name);
    const presign = await getPresignedUploadUrl(uploadName, file.type, file.size);
    if (!presign || !presign.url) throw new Error("presign unavailable");
    await putToS3(presign.url, file, onProgress, onXhr);
    const crc = await crc32Hex(file);
    return {
      uploadName,
      crc,
      s3Url: presign.sourceUrl,
      imgixUrl: `https://${IMGIX_HOST}/${presign.path || uploadName}`
    };
  }
  var ldlFracs = /* @__PURE__ */ new Map();
  var ldlCancelled = false;
  var activeBatches = 0;
  var ldlT0 = 0;
  var ldlEta = 0;
  var ldlTarget = 0;
  var ldlShown = 0;
  var ldlDriver = 0;
  var activeXhrs = /* @__PURE__ */ new Set();
  function ldlReset() {
    ldlFracs.clear();
    ldlCancelled = false;
    ldlT0 = Date.now();
    ldlEta = ldlTarget = ldlShown = 0;
    clearInterval(ldlDriver);
    ldlDriver = setInterval(() => {
      const cap = ldlTarget >= 95 ? 99 : ldlTarget;
      ldlShown = Math.min(ldlShown + Math.max(0.3, (cap - ldlShown) * 0.12), cap);
      ldlWrite(Math.round(ldlShown));
    }, 120);
    ldlWrite(0);
  }
  function ldlDone() {
    clearInterval(ldlDriver);
    ldlShown = ldlTarget = 100;
    ldlWrite(100);
  }
  document.addEventListener("bsv4:preview-ready", ldlDone);
  function ldlReport(id, frac) {
    ldlFracs.set(id, Math.max(ldlFracs.get(id) || 0, frac));
    let sum = 0;
    for (const v of ldlFracs.values()) sum += v;
    ldlTarget = Math.round(sum / ldlFracs.size * 100);
  }
  function ldlWrite(pct) {
    if (!els || !els.root) return;
    let eta = "";
    const tp = ldlTarget;
    if (ldlT0 && tp >= 3 && tp <= 90) {
      const remain = (Date.now() - ldlT0) / tp * (100 - tp) / 1e3;
      ldlEta = ldlEta ? ldlEta * 0.6 + remain * 0.4 : remain;
      if (ldlEta > 8) {
        eta = ldlEta > 90 ? `about ${Math.round(ldlEta / 60)}m left` : `about ${Math.ceil(ldlEta / 5) * 5}s left`;
      }
    }
    for (const node of els.root.querySelectorAll("[data-bsv4-upprog]")) {
      node.textContent = `${pct}%`;
    }
    for (const node of els.root.querySelectorAll("[data-bsv4-upeta]")) {
      node.textContent = eta;
    }
    for (const bar of els.root.querySelectorAll(".bsv4-preview__ringbar")) {
      bar.style.strokeDashoffset = 427.26 * (1 - pct / 100);
    }
  }
  function installCancelHook() {
    window.resetLoading = () => {
      ldlCancelled = true;
      for (const x of activeXhrs) {
        try {
          x.abort();
        } catch (e) {
        }
      }
      activeXhrs.clear();
    };
  }
  function blobUrlFor(file) {
    try {
      return /\.(png|jpe?g|svg)$/i.test(file.name || "") ? URL.createObjectURL(file) : null;
    } catch (e) {
      return null;
    }
  }
  async function uploadOne(file) {
    const id = `f-${++seq}`;
    const localUrl = blobUrlFor(file);
    announce("upload-started", { id, name: file.name, localUrl });
    ldlReport(id, 0);
    try {
      const up = await uploadBlob(
        file,
        (frac) => {
          ldlReport(id, frac * 0.9);
        },
        (xhr) => activeXhrs.add(xhr)
      );
      if (ldlCancelled) return;
      const { uploadName, crc } = up;
      const rawExt = (file.name.split(".").pop() || "").toLowerCase();
      const ext = rawExt === "jpeg" ? "jpg" : rawExt === "tiff" ? "tif" : rawExt;
      const params = (cfg.imgixParams || {})[ext] || {};
      if (params.preview) {
        const bgR = !!params.bgRemoverDefault && cfg.preview?.bgRemove !== false;
        new Image().src = `${up.imgixUrl}?${params.preview}${bgR ? "&bg-remove=true" : ""}`;
      }
      const record = {
        id,
        name: file.name,
        uploadName,
        ext,
        s3Url: up.s3Url,
        imgixUrl: up.imgixUrl,
        crc,
        params,
        // The matrix's per-type default only applies when the preview block's
        // Background Remove toggle is on — a merchant turning the feature OFF
        // must not get silently bg-removed art with the control hidden.
        bgRemove: !!params.bgRemoverDefault && cfg.preview?.bgRemove !== false,
        superRes: false,
        // Attribution riders (cart-payload.js:126-129 stamps from these): the
        // AI shims tag the File object (manageFiles direct, uploadFile via the
        // one-shot window-flag consume above).
        isAIImage: file.isAIImage || void 0,
        isAIEdit: file.isAIEdit || void 0,
        rows: []
      };
      Object.assign(record, await resolveDimensions(record, params.preview));
      if (record.inW != null) record.widthIn = Number(record.inW);
      if (record.inH != null) record.heightIn = Number(record.inH);
      ldlReport(id, 0.95);
      if (ldlCancelled) return;
      update({ files: [...getState().files, record] });
      announce("file-uploaded", { id, name: file.name, inW: record.inW, inH: record.inH });
      saveFileToUser({ customerId: cfg.customerId, productId: cfg.productId, fileRecord: record });
    } catch (err) {
      if (err && err.name === "AbortError") return;
      showError(`${file.name}: ${err.message || "upload failed"} \u2014 please try again.`);
      announce("file-failed", { id, name: file.name, error: String(err && err.message) });
    } finally {
      if (localUrl) setTimeout(() => URL.revokeObjectURL(localUrl), 6e4);
    }
  }
  async function intakeFromUrls(entries) {
    if (!els) return;
    if (!entries.length) return;
    if (getState().status === "uploading" && getState().mode !== "multi") return;
    const cap = resolveIntakeCap(cfg);
    const room = cap - getState().files.length;
    if (entries.length > room) {
      showError(
        cap === 1 ? "This product takes one design at a time \u2014 only the first selection was kept." : `Maximum ${cap} designs per order \u2014 ${entries.length - Math.max(0, room)} selection(s) skipped.`
      );
      entries = entries.slice(0, Math.max(0, room));
      if (!entries.length) return;
    }
    if (entries.length === 1 && entries[0].orderName) {
      setAttribution({ designType: 1, orderName: entries[0].orderName });
    }
    update({
      status: "uploading",
      mode: getState().mode === "multi" || entries.length > 1 || getState().files.length ? "multi" : "single"
    });
    announce("files-received", { count: entries.length, source: "previous-designs" });
    ldlReset();
    activeBatches++;
    try {
      await Promise.all(
        entries.map(async (entry) => {
          const id = `f-${++seq}`;
          const clean = entry.url.split("?")[0];
          const uploadName = clean.split("/").pop();
          const rawExt = (uploadName.split(".").pop() || "").toLowerCase();
          const ext = rawExt === "jpeg" ? "jpg" : rawExt === "tiff" ? "tif" : rawExt;
          announce("upload-started", { id, name: entry.name || uploadName });
          ldlReport(id, 0.5);
          try {
            let params = (cfg.imgixParams || {})[ext] || {};
            if (entry.isReadyToPress === "Yes") {
              params = { ...params, order: "[S3]" };
            }
            const record = {
              id,
              name: entry.name || uploadName,
              uploadName,
              ext,
              s3Url: clean.split(IMGIX_HOST).join(S3_HOST),
              imgixUrl: clean.split(S3_HOST).join(IMGIX_HOST),
              crc: null,
              params,
              isReadyToPress: entry.isReadyToPress,
              isDesignStudio: entry.isDesignStudio,
              isAIImage: entry.isAIImage,
              isAIEdit: entry.isAIEdit,
              bgRemove: false,
              superRes: false,
              rows: []
            };
            Object.assign(record, await resolveDimensions(record, record.params.preview));
            if (record.inW != null) record.widthIn = Number(record.inW);
            if (record.inH != null) record.heightIn = Number(record.inH);
            ldlReport(id, 0.95);
            if (ldlCancelled) return;
            update({ files: [...getState().files, record] });
            announce("file-uploaded", { id, name: record.name, inW: record.inW, inH: record.inH });
          } catch (err) {
            showError(`${entry.name || uploadName}: ${err.message || "could not load design"}`);
            announce("file-failed", { id, name: entry.name || uploadName, error: String(err && err.message) });
          }
        })
      );
    } finally {
      activeBatches--;
    }
    settleBatch();
  }
  function activeUploadZone() {
    if (!els) return null;
    const modalAdd = els.root && els.root.querySelector("[data-bsv4-modal-addfile]");
    if (getState().mode === "multi" && modalAdd && modalAdd.offsetParent !== null) return modalAdd;
    const add = els.root && els.root.querySelector("[data-bsv4-add-another]");
    if (getState().status !== "empty" && add && add.offsetParent !== null) return add;
    return els.zone;
  }
  function fileTypesSentence() {
    const list = allowedExts();
    if (!list.length) return "That file type is not supported.";
    const labels = [];
    const used = /* @__PURE__ */ new Set();
    for (const e of list) {
      if (used.has(e)) continue;
      if (e === "jpg" || e === "jpeg") {
        labels.push("JPG / JPEG");
        used.add("jpg");
        used.add("jpeg");
      } else if (e === "tif" || e === "tiff") {
        labels.push("TIF / TIFF");
        used.add("tif");
        used.add("tiff");
      } else {
        labels.push(e.toUpperCase());
        used.add(e);
      }
    }
    const last = labels.pop();
    const joined = labels.length ? `${labels.join(", ")} and ${last}` : last;
    return `We accept the following file types: ${joined}`;
  }
  var errCardTimer = null;
  function showUploadErrorCard(zone, message) {
    if (!zone) return;
    clearTimeout(errCardTimer);
    const prev = zone.querySelector(":scope > .bsv4-uperr");
    if (prev) prev.remove();
    zone.classList.add("bsv4-uperr-host");
    const card = document.createElement("div");
    card.className = "bsv4-uperr";
    card.setAttribute("role", "alert");
    card.innerHTML = '<span class="bsv4-uperr__circle" aria-hidden="true"><svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="#e5484d" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5"/><path d="M12 16.4v.1"/></svg></span><p class="bsv4-uperr__head">Couldn\u2019t upload your files</p><p class="bsv4-uperr__msg"></p><button type="button" class="bsv4-uperr__dismiss">Dismiss</button>';
    card.querySelector(".bsv4-uperr__msg").textContent = message;
    const close = () => {
      clearTimeout(errCardTimer);
      card.remove();
      zone.classList.remove("bsv4-uperr-host");
    };
    card.addEventListener("click", (e) => e.stopPropagation());
    card.querySelector(".bsv4-uperr__dismiss").addEventListener("click", (e) => {
      e.stopPropagation();
      close();
    });
    zone.appendChild(card);
    errCardTimer = setTimeout(close, 4e3);
  }
  function showError(msg) {
    showUploadErrorCard(activeUploadZone(), msg);
  }

  // src/preview.js
  var NINJA_GIF = "https://cdn.shopify.com/s/files/1/0558/0265/8899/files/ninja-July-4.gif?v=1782765055";
  var cfg2;
  var stagePending = null;
  var stageCanvas = null;
  var stageDocBound = false;
  function initPreview(root3, config) {
    if (config) cfg2 = config;
    const block = root3.querySelector('[data-bsv4-block="upload_preview"]');
    if (!block || !cfg2 || !cfg2.preview) return;
    if (block.dataset.bsv4PreviewInit === "1") return;
    block.dataset.bsv4PreviewInit = "1";
    const box = block.querySelector(".bsv4-preview");
    const canvas = block.querySelector("[data-bsv4-canvas]");
    const tools = block.querySelector("[data-bsv4-bgr-host]");
    if (!box || !canvas) return;
    stageCanvas = canvas;
    if (!stageDocBound) {
      stageDocBound = true;
      document.addEventListener("bsv4:upload-started", (e) => {
        const d = e.detail || {};
        if (!d.localUrl || getState().mode !== "single" || !stageCanvas) return;
        const loader = stageCanvas.querySelector(".bsv4-preview__loading");
        if (!loader || loader.querySelector(".bsv4-preview__loading-art")) return;
        const img = document.createElement("img");
        img.className = "bsv4-preview__loading-art";
        img.alt = "";
        img.style.cssText = "position:absolute;inset:0;margin:auto;width:auto;height:auto;max-width:74cqw;max-height:74cqh;object-fit:contain";
        img.src = d.localUrl;
        loader.prepend(img);
      });
    }
    box.classList.toggle("bsv4-preview--no-bgr", !cfg2.preview.bgRemove);
    box.classList.toggle("bsv4-preview--no-upscale", !cfg2.preview.upscale);
    box.classList.toggle("bsv4-preview--no-aiedit", !cfg2.preview.aiEdit);
    const zoom = document.createElement("dialog");
    zoom.className = "bsv4-zoom";
    zoom.setAttribute("data-bsv4-zoom", "");
    zoom.setAttribute("aria-label", "Zoomed design preview");
    zoom.innerHTML = `<img alt=""><p>Click anywhere to close</p>`;
    block.appendChild(zoom);
    zoom.addEventListener("click", () => zoom.close());
    canvas.addEventListener("click", (e) => {
      const file = activeFile(getState());
      if (!file) return;
      if (e.target.closest("[data-bsv4-file-delete]")) {
        deleteFile(file);
      } else if (e.target.closest("[data-bsv4-zoom-open]") || e.target.matches(".bsv4-preview__art img")) {
        const img = zoom.querySelector("img");
        img.alt = file.name || "";
        swapImage(img, previewUrl(file), zoom);
        zoom.showModal();
      }
    });
    subscribe((state2) => {
      const file = activeFile(state2);
      if (tools && tools.dataset.bsv4BgrFor && (!file || tools.dataset.bsv4BgrFor !== file.id)) {
        tools.innerHTML = "";
        delete tools.dataset.bsv4BgrFor;
      }
      if (!file || !file.imgixUrl) {
        stagePending = null;
        if (state2.status === "uploading") {
          if (!canvas.querySelector(".bsv4-preview__loading")) {
            canvas.innerHTML = "";
            canvas.appendChild(buildStageLoader());
          }
        } else {
          canvas.innerHTML = "";
        }
        return;
      }
      const url = previewUrl(file);
      if (!canvas.querySelector(".bsv4-preview__art")) {
        if (stagePending !== url) {
          stagePending = url;
          let loader = canvas.querySelector(".bsv4-preview__loading");
          if (!loader) {
            canvas.innerHTML = "";
            loader = buildStageLoader();
            canvas.appendChild(loader);
          }
          loader.classList.remove("is-done");
          const pre = new Image();
          pre.onload = pre.onerror = () => {
            if (stagePending !== url) return;
            announce("preview-ready");
            loader.classList.add("is-done");
            setTimeout(() => {
              if (stagePending !== url) return;
              stagePending = null;
              buildArt(canvas);
              const ph = canvas.querySelector(".bsv4-preview__frame img");
              if (pre.naturalWidth) {
                pre.alt = file.name || "";
                ph.replaceWith(pre);
              } else {
                ph.alt = file.name || "";
                swapImage(ph, url, canvas.querySelector(".bsv4-preview__frame"));
              }
              const d = activeDims(file);
              setDim(canvas.querySelector("[data-bsv4-dim-w]"), d.w);
              setDim(canvas.querySelector("[data-bsv4-dim-h]"), d.h);
            }, 220);
          };
          pre.src = url;
        }
        return;
      }
      const img = canvas.querySelector("img");
      img.alt = file.name || "";
      swapImage(img, url, canvas.querySelector(".bsv4-preview__frame"));
      const dims = activeDims(file);
      setDim(canvas.querySelector("[data-bsv4-dim-w]"), dims.w);
      setDim(canvas.querySelector("[data-bsv4-dim-h]"), dims.h);
    });
    initMobileTabs(root3, box);
  }
  function buildArt(canvas) {
    canvas.innerHTML = `
    <figure class="bsv4-preview__art">
      <span class="bsv4-preview__actions">
        <button type="button" data-bsv4-file-delete aria-label="Delete design" title="Delete design">
          <svg viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2"><path fill="currentColor" fill-rule="nonzero" d="m4.015 5.494h-.253c-.413 0-.747-.335-.747-.747s.334-.747.747-.747h5.253v-1c0-.535.474-1 1-1h4c.526 0 1 .465 1 1v1h5.254c.412 0 .746.335.746.747s-.334.747-.746.747h-.254v15.435c0 .591-.448 1.071-1 1.071-2.873 0-11.127 0-14 0-.552 0-1-.48-1-1.071zm14.5 0h-13v15.006h13zm-4.25 2.506c-.414 0-.75.336-.75.75v8.5c0 .414.336.75.75.75s.75-.336.75-.75v-8.5c0-.414-.336-.75-.75-.75zm-4.5 0c-.414 0-.75.336-.75.75v8.5c0 .414.336.75.75.75s.75-.336.75-.75v-8.5c0-.414-.336-.75-.75-.75zm3.75-4v-.5h-3v.5z"/></svg>
        </button>
        <button type="button" data-bsv4-zoom-open aria-label="Zoom design" title="Zoom">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/></svg>
        </button>
      </span>
      <span class="bsv4-preview__frame">
        <span class="bsv4-preview__dim bsv4-preview__dim--w" data-bsv4-dim-w hidden><i></i></span>
        <span class="bsv4-preview__dim bsv4-preview__dim--h" data-bsv4-dim-h hidden><i></i></span>
        <img alt="">
      </span>
      <span class="bsv4-preview__pill"><b>Preview:</b> The image will print as shown above.</span>
    </figure>`;
  }
  function initMobileTabs(root3, box) {
    const guide = root3.querySelector('[data-bsv4-block="size_guide"]');
    const body = box.querySelector(".bsv4-preview__body");
    const tabs = box.querySelector("[data-bsv4-mobtabs]");
    if (!guide || !body || !tabs) return;
    const home = { parent: guide.parentNode, next: guide.nextSibling };
    const mq = window.matchMedia("(max-width: 999px)");
    const setView = (view) => {
      box.dataset.bsv4Mobview = view === "guide" ? "guide" : "customize";
      tabs.querySelectorAll("[data-bsv4-mobtab]").forEach((b) => {
        b.classList.toggle("is-active", b.dataset.bsv4Mobtab === box.dataset.bsv4Mobview);
      });
    };
    const place = () => {
      if (mq.matches) {
        if (guide.parentNode !== box) box.insertBefore(guide, body.nextSibling);
        if (!box.dataset.bsv4Mobview) setView("customize");
      } else if (guide.parentNode === box) {
        home.parent.insertBefore(guide, home.next);
      }
    };
    tabs.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-bsv4-mobtab]");
      if (btn) setView(btn.dataset.bsv4Mobtab);
    });
    mq.addEventListener("change", place);
    place();
  }
  function buildStageLoader() {
    const card = document.createElement("div");
    card.className = "bsv4-preview__loading";
    card.setAttribute("role", "status");
    card.innerHTML = '<span class="bsv4-preview__ring" aria-hidden="true"><svg viewBox="0 0 150 150"><circle cx="75" cy="75" r="68" fill="none" stroke="#dfe5ea" stroke-width="7"/><circle class="bsv4-preview__ringbar" cx="75" cy="75" r="68" fill="none" stroke="#0177C4" stroke-width="7" stroke-linecap="round" stroke-dasharray="427.26"/></svg><img class="bsv4-preview__loading-ninja" alt="" width="200" height="200"></span><span class="bsv4-preview__pill bsv4-preview__lpill"><b>0%</b><i></i><button type="button">Cancel</button></span>';
    const CENTER = "position:absolute;inset:0;margin:auto;";
    card.querySelector(".bsv4-preview__ring").style.cssText = CENTER + "width:150px;height:150px";
    card.querySelector("svg").style.cssText = "display:block;transform:rotate(-90deg)";
    const bar = card.querySelector(".bsv4-preview__ringbar");
    bar.style.transition = "stroke-dashoffset 0.2s";
    bar.style.strokeDashoffset = 427.26;
    const gif = card.querySelector("img");
    gif.src = NINJA_GIF;
    gif.style.cssText = CENTER + "width:124px;height:124px;border-radius:50%";
    card.querySelector("b").setAttribute("data-bsv4-upprog", "");
    card.querySelector("i").setAttribute("data-bsv4-upeta", "");
    card.querySelector("button").addEventListener("click", () => {
      if (getState().status === "uploading") window.resetLoading?.();
      else reset();
    });
    return card;
  }
  function setDim(el, val) {
    if (!el) return;
    el.hidden = val == null;
    if (val != null && el.firstElementChild) el.firstElementChild.textContent = `${val}"`;
  }
  function activeFile(state2) {
    return state2.activeFileId && state2.files.find((f) => f.id === state2.activeFileId) || state2.files[0] || null;
  }
  function activeDims(file) {
    const tab = getState().sizeTab === "popular" ? "popular" : "custom";
    const rows = (file.rows || []).filter((r) => (r.mode || "custom") === tab && r.w > 0);
    const row = rows[rows.length - 1];
    const w = row ? row.w : file.widthIn != null ? file.widthIn : file.inW;
    const h = row ? row.h : file.heightIn != null ? file.heightIn : file.inH;
    return { w: fmtIn(w), h: fmtIn(h) };
  }
  function fmtIn(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(2) : null;
  }
  function normalizeExt(file) {
    const raw = (file.ext || file.type || (file.name || "").split(".").pop() || "").toLowerCase();
    return raw === "jpeg" ? "jpg" : raw === "tiff" ? "tif" : raw;
  }
  function paramsFor(file, previewCfg) {
    if (file.params && Object.keys(file.params).length) return file.params;
    return previewCfg && previewCfg.imgix && previewCfg.imgix[normalizeExt(file)] || {};
  }
  function buildPreviewUrl(file, params) {
    if (file.bgRemove && file.bgrModel) {
      const pers = file.bgrVersions && file.bgrVersions[file.bgrModel];
      const disp = file.bgrDisplay && file.bgrDisplay[file.bgrModel];
      if (file.superRes && pers) return `${pers}&upscale=true`;
      if (disp || pers) return disp || pers;
    }
    const url = file.imgixUrl;
    if (!url || /^(data|blob):/.test(url)) return url;
    const parts = [];
    if (params.preview) parts.push(params.preview);
    if (file.bgRemove) parts.push("bg-remove=true");
    if (file.superRes) parts.push("upscale=true");
    return parts.length ? `${url}?${parts.join("&")}` : url;
  }
  function previewUrl(file) {
    return buildPreviewUrl(file, paramsFor(file, cfg2 && cfg2.preview));
  }
  var swapState = /* @__PURE__ */ new WeakMap();
  function swapImage(img, url, host2) {
    if (!img || !url) return;
    let st = swapState.get(img);
    if (!st) {
      st = { painted: img.getAttribute("src") || null, pending: null, wait: null };
      swapState.set(img, st);
    }
    const settle = () => {
      if (st.wait) {
        st.wait.remove();
        st.wait = null;
      }
    };
    if (url === st.painted) {
      st.pending = null;
      settle();
      return;
    }
    if (url === st.pending) return;
    st.pending = url;
    if (!st.painted) img.style.visibility = "hidden";
    if (host2 && !st.wait) {
      st.wait = document.createElement("span");
      st.wait.className = "bsv4-preview__imgwait";
      st.wait.setAttribute("aria-hidden", "true");
      st.wait.innerHTML = '<span class="bsv4-preview__loading-spin"></span>';
      host2.appendChild(st.wait);
    }
    const pre = new Image();
    pre.onload = () => {
      if (st.pending !== url) return;
      img.src = url;
      img.style.visibility = "";
      st.painted = url;
      st.pending = null;
      settle();
    };
    pre.onerror = () => {
      if (st.pending !== url) return;
      if (st.retried !== url) {
        st.retried = url;
        setTimeout(() => {
          if (st.pending !== url) return;
          pre.src = "";
          pre.src = url;
        }, 4e3);
        return;
      }
      st.pending = null;
      settle();
    };
    pre.src = url;
  }
  function deleteFile(file) {
    const files = getState().files.filter((f) => f.id !== file.id);
    if (!files.length) {
      reset();
    } else {
      update({ files });
    }
    announce("rows-changed", {});
    announce("file-removed", { id: file.id, name: file.name });
  }

  // src/ported/sizing-math.js
  function matchCustomVariant(variants, widthIn, heightIn) {
    let area = parseFloat(widthIn) * parseFloat(heightIn);
    if (!(area >= 1)) area = 1;
    area = Math.ceil(area);
    const customs = variants.filter((v) => v.isCustom);
    if (!customs.length) return null;
    const highestMax = Math.max(...customs.map((v) => v.maxIn));
    if (area > highestMax) {
      return customs.reduce((max, cur) => cur.maxIn > max.maxIn ? cur : max);
    }
    return customs.slice().reverse().find((v) => area >= v.minIn && area <= v.maxIn) || null;
  }
  function parsePopularDims(option1) {
    const m = /^([\d.]+)[\^"]?\s*x\s*([\d.]+)[\^"]?/i.exec((option1 || "").trim());
    return m ? { w: parseFloat(m[1]), h: parseFloat(m[2]) } : null;
  }
  function containFit(designW, designH, boxW, boxH) {
    const maxW = parseFloat(boxW);
    const maxH = parseFloat(boxH);
    if (!maxW || !maxH || !designW || !designH) return { w: maxW, h: maxH };
    const aspect = designW / designH;
    let w = maxW;
    let h = w / aspect;
    if (h > maxH) {
      h = maxH;
      w = h * aspect;
    }
    return { w: parseFloat(w.toFixed(2)), h: parseFloat(h.toFixed(2)) };
  }
  function clampToMax(w, h, maxW, maxH) {
    w = parseFloat(w);
    h = parseFloat(h);
    if (!Number.isFinite(w) || !Number.isFinite(h) || !(w > 0) || !(h > 0)) return null;
    if (w <= maxW && h <= maxH) return { w, h, clamped: false };
    const scale = Math.min(maxW / w, maxH / h);
    return {
      w: parseFloat((w * scale).toFixed(2)),
      h: parseFloat((h * scale).toFixed(2)),
      clamped: true
    };
  }
  function lockedCounterpart(editedValue, editedAxis, designW, designH) {
    const ratio = editedAxis === "w" ? designH / designW : designW / designH;
    return parseFloat((editedValue * ratio).toFixed(2));
  }
  function parseTiers(tiers2) {
    return tiers2.map((t, i) => {
      const label = String(t.qty).replace(/\s|pcs?/gi, "");
      let min;
      let max;
      if (label.includes("+")) {
        min = parseInt(label, 10);
        max = 1e6;
      } else if (label.includes("-")) {
        const [a, b] = label.split("-");
        min = parseInt(a, 10);
        max = parseInt(b, 10);
      } else {
        min = max = parseInt(label, 10);
      }
      const next = tiers2[i + 1];
      return { index: i + 1, min, max, off: t.pct || 0, nextOff: next ? next.pct || 0 : null };
    });
  }
  function tierFor(parsedTiers2, totalQty) {
    return parsedTiers2.find((t) => totalQty >= t.min && totalQty <= t.max) || null;
  }
  function unitsToNextTier(tier, pageQty2, cartQty) {
    const state2 = cartQty + pageQty2;
    if (pageQty2 === 1) return tier.max + 2 - state2;
    if (tier.max - state2 === 0) return 1;
    return tier.max - state2 + 1;
  }
  function dpiWarns(mode, pxW, pxH, inW, inH) {
    if (mode === "custom") {
      const uploadedUnits = pxW * pxH / 72;
      const requestedUnits = inW * inH * 72;
      if (uploadedUnits < 288) return true;
      if (uploadedUnits > 288 && uploadedUnits > requestedUnits) return false;
      return true;
    }
    const boxArea = inW * inH;
    const nativeArea = pxW / 72 * (pxH / 72);
    if (nativeArea < 4) return true;
    if (boxArea < nativeArea) return false;
    return true;
  }
  function discounted(unitPrice, offPct) {
    const unit = parseFloat(unitPrice) || 0;
    return unit - unit * (offPct || 0) / 100;
  }

  // src/sizing.js
  var cfg3;
  var sectionRoot;
  var instances = [];
  var docBound = false;
  var rowSeq = 0;
  var debounces = /* @__PURE__ */ new Map();
  var pendingInputs = /* @__PURE__ */ new Set();
  function resolveLimits(instanceCfg, globalLimits) {
    const g = globalLimits || {};
    const num = (v) => {
      const n = typeof v === "string" && v.trim() !== "" ? parseFloat(v) : v;
      return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
    };
    const minIn = num(g.minIn) ?? 0.25;
    const maxIn = num(g.maxIn) ?? 22;
    const i = instanceCfg || {};
    return {
      minW: num(i.minW) ?? minIn,
      maxW: num(i.maxW) ?? maxIn,
      minH: num(i.minH) ?? minIn,
      maxH: num(i.maxH) ?? maxIn,
      minQty: num(i.minQty) ?? num(g.minQty) ?? 1
    };
  }
  function popularCapFor(popular, w, h) {
    if (!Array.isArray(popular)) return null;
    return popular.find((c) => c.w === w && c.h === h) || null;
  }
  function effectiveMax(lim, cap) {
    return {
      maxW: Math.min(lim.maxW, cap && Number.isFinite(cap.maxW) ? cap.maxW : Infinity),
      maxH: Math.min(lim.maxH, cap && Number.isFinite(cap.maxH) ? cap.maxH : Infinity)
    };
  }
  function computeLockedDims(value, axis, aspectW, aspectH, lim) {
    let val = parseFloat(value);
    if (!Number.isFinite(val) || val <= 0) return null;
    let err = null;
    const minEdit = axis === "w" ? lim.minW : lim.minH;
    const maxEdit = axis === "w" ? lim.maxW : lim.maxH;
    if (minEdit && val < minEdit) {
      val = minEdit;
      err = `${minEdit}" minimum`;
    }
    if (val > maxEdit) {
      val = maxEdit;
      err = `${maxEdit}" max`;
    }
    const other = lockedCounterpart(val, axis, aspectW, aspectH);
    let w = axis === "w" ? val : other;
    let h = axis === "w" ? other : val;
    const minOther = axis === "w" ? lim.minH : lim.minW;
    const maxOther = axis === "w" ? lim.maxH : lim.maxW;
    const counter = axis === "w" ? h : w;
    if (counter > maxOther) {
      const fit = clampToMax(w, h, lim.maxW, lim.maxH);
      w = fit.w;
      h = fit.h;
      err = `${maxOther}" max`;
    } else if (minOther && counter < minOther) {
      const scale = minOther / counter;
      w = parseFloat((w * scale).toFixed(2));
      h = parseFloat((h * scale).toFixed(2));
      err = `${minOther}" minimum`;
      const fit = clampToMax(w, h, lim.maxW, lim.maxH);
      if (fit && fit.clamped) {
        w = fit.w;
        h = fit.h;
      }
    }
    return { w, h, err };
  }
  function fileDims(file) {
    return {
      w: parseFloat(file.inW ?? file.widthIn) || 0,
      h: parseFloat(file.inH ?? file.heightIn) || 0
    };
  }
  function fileAspect(file) {
    if (file.pxW && file.pxH) return { w: file.pxW, h: file.pxH };
    const d = fileDims(file);
    return { w: d.w || 1, h: d.h || 1 };
  }
  function fileExt(file) {
    return (file.ext || file.type || "").toLowerCase();
  }
  function initSizing(root3, config) {
    cfg3 = config;
    sectionRoot = root3;
    instances = [];
    const entries = Array.isArray(cfg3.sizing) ? cfg3.sizing : [];
    if (!entries.length) return;
    root3.querySelectorAll('[data-bsv4-block="size_inputs"]').forEach((el, i) => {
      const settings = entries[Math.min(i, entries.length - 1)];
      const inst = {
        el,
        settings,
        lim: resolveLimits(settings, cfg3.limits),
        rows: el.querySelector("[data-bsv4-rows]"),
        template: el.querySelector("[data-bsv4-row-template]"),
        tab: "custom"
      };
      if (!inst.rows || !inst.template) return;
      instances.push(inst);
    });
    if (!instances.length) return;
    if (root3.dataset.bsv4SizingBound !== "1") {
      root3.dataset.bsv4SizingBound = "1";
      root3.addEventListener("input", onInput);
      root3.addEventListener("change", onChange);
      root3.addEventListener("click", onClick);
      const selectAll = (e) => {
        if (e.target.matches && e.target.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) {
          e.target.select();
        }
      };
      root3.addEventListener("focusin", selectAll);
      root3.addEventListener("click", selectAll);
    }
    if (!docBound) {
      docBound = true;
      document.addEventListener("bsv4:file-uploaded", onFileUploaded);
      document.addEventListener("bsv4:price-changed", () => syncAll());
      subscribe(() => syncAll());
    }
    syncAll();
  }
  function onFileUploaded(e) {
    const file = getState().files.find((f) => f.id === e.detail.id);
    if (!file) return;
    if (getState().sizeTab !== "custom") update({ sizeTab: "custom" });
    const tileRows = sectionRoot.querySelector(
      `[data-bsv4-tile="${file.id}"] [data-bsv4-tile-rows]`
    );
    const d = fileDims(file);
    addRow(file, { w: d.w, h: d.h }, tileRows || void 0);
  }
  function instanceFor(el) {
    return el && instances.find((inst) => inst.el.contains(el)) || null;
  }
  function addRow(file, dims, container, via, modeOverride) {
    const inst = via || instanceFor(container) || instances[0];
    if (!inst) return null;
    const mode = modeOverride === "popular" || modeOverride === "custom" ? modeOverride : getState().sizeTab === "popular" ? "popular" : "custom";
    let row;
    if (mode === "popular") {
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode,
        w: 0,
        h: 0,
        qty: inst.lim.minQty,
        variantId: null
      };
    } else {
      const max = effectiveMax(inst.lim, null);
      const clamped = clampToMax(dims.w, dims.h, max.maxW, max.maxH) || dims;
      const variant = matchCustomVariant(cfg3.variants || [], clamped.w, clamped.h);
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode,
        w: clamped.w,
        h: clamped.h,
        qty: inst.lim.minQty,
        variantId: variant ? variant.id : null
      };
    }
    file.rows.push(row);
    if (container) renderRow(file, row, container);
    commit();
    return row;
  }
  var POPULAR_DESC = {
    '2" x 2"': "Tag Print / Neck Label",
    '3" x 3"': "Small Tag/Sleeve Icon",
    '4" x 2"': "Name Label/Small Chest Text",
    '4" x 4"': "Left-Chest Logo",
    '5" x 3"': "Small Badge Logo",
    '5" x 5"': "Small Chest/Youth Print",
    '6" x 6"': "Std Left-Chest/Youth",
    '7" x 7"': "Small Front (Adult)",
    '8" x 8"': "Medium Front Print",
    '9" x 9"': "Large Chest/Med Front",
    '9" x 11"': "Vertical Front/Youth",
    '10" x 10"': "Standard Adult Front",
    '11" x 5"': "Chest Banner/Sleeve",
    '11" x 11"': "Adult Full Front",
    '11" x 14"': "Tall Front/Jersey",
    '12" x 17"': "Oversized Front/Hoodie"
  };
  function renderRow(file, row, container) {
    const inst = instanceFor(container) || instances[0];
    if (!inst) return null;
    const node = inst.template.content.firstElementChild.cloneNode(true);
    node.dataset.bsv4RowId = row.id;
    const select = node.querySelector("[data-bsv4-popular]");
    if (select) {
      select.innerHTML = "";
      const ph = document.createElement("option");
      ph.value = "";
      ph.textContent = "Select a popular size";
      ph.disabled = true;
      ph.selected = true;
      select.appendChild(ph);
      for (const v of cfg3.variants || []) {
        if (!v.isPopular) continue;
        const opt = document.createElement("option");
        opt.value = v.option1;
        const desc = POPULAR_DESC[v.option1];
        opt.textContent = desc ? `${v.option1} - ${desc}` : v.option1;
        opt.dataset.orig = opt.textContent;
        select.appendChild(opt);
      }
    }
    applyTabToNode(node, row.mode || "custom");
    syncRowDom(node, file, row);
    container.appendChild(node);
    return node;
  }
  function syncAll() {
    if (!instances.length) return;
    const state2 = getState();
    const tab = state2.sizeTab === "popular" ? "popular" : "custom";
    for (const inst of instances) {
      inst.el.querySelectorAll("[data-bsv4-size-tab]").forEach((t) => {
        t.classList.toggle("is-active", t.dataset.bsv4SizeTab === tab);
      });
      const intro = inst.el.querySelector(".bsv4-sizing__intro");
      if (intro) applyTabToNode(intro, tab);
    }
    const valid = /* @__PURE__ */ new Set();
    for (const file of state2.files) {
      for (const row of file.rows) {
        if (!row.id) row.id = `r-${++rowSeq}`;
        valid.add(row.id);
      }
    }
    sectionRoot.querySelectorAll("[data-bsv4-row-id]").forEach((n) => {
      if (!valid.has(n.dataset.bsv4RowId)) n.remove();
    });
    const tiled = /* @__PURE__ */ new Set();
    if (state2.mode === "multi" && state2.files.length) {
      sectionRoot.querySelectorAll("[data-bsv4-tile] [data-bsv4-row-id]").forEach((n) => {
        tiled.add(n.dataset.bsv4RowId);
      });
    } else {
      sectionRoot.querySelectorAll("[data-bsv4-tile] [data-bsv4-row-id]").forEach((n) => n.remove());
    }
    for (const inst of instances) {
      const keep = /* @__PURE__ */ new Set();
      let cursor = null;
      for (const file of state2.files) {
        for (const row of file.rows) {
          if (tiled.has(row.id)) continue;
          let node = inst.rows.querySelector(`[data-bsv4-row-id="${row.id}"]`);
          if (node) syncRowDom(node, file, row);
          else node = renderRow(file, row, inst.rows);
          keep.add(node);
          const expected = cursor ? cursor.nextElementSibling : inst.rows.firstElementChild;
          if (node !== expected) inst.rows.insertBefore(node, expected);
          cursor = node;
        }
      }
      inst.rows.querySelectorAll("[data-bsv4-row-id]").forEach((n) => {
        if (!keep.has(n)) n.remove();
      });
    }
    if (state2.mode === "multi") {
      for (const file of state2.files) {
        const host2 = sectionRoot.querySelector(
          `[data-bsv4-tile="${file.id}"] [data-bsv4-tile-rows]`
        );
        if (!host2) continue;
        const firstRow = host2.querySelector("[data-bsv4-row-id]");
        if (!firstRow) continue;
        let cursor = firstRow.previousElementSibling;
        for (const row of file.rows) {
          const node = host2.querySelector(`[data-bsv4-row-id="${row.id}"]`);
          if (!node) continue;
          const expected = cursor ? cursor.nextElementSibling : host2.firstElementChild;
          if (node !== expected) host2.insertBefore(node, expected);
          cursor = node;
        }
      }
    }
    sectionRoot.querySelectorAll("[data-bsv4-row-id]").forEach((n) => {
      if (state2.mode === "multi" && n.closest("[data-bsv4-tile]")) return;
      const id = n.dataset.bsv4RowId;
      let mode = "custom";
      for (const file of state2.files) {
        const row = (file.rows || []).find((r) => r.id === id);
        if (row) {
          mode = row.mode || "custom";
          break;
        }
      }
      const off = mode !== tab;
      n.classList.toggle("is-disabled", off);
      n.querySelectorAll("input, select, button").forEach((el) => {
        el.disabled = off;
      });
    });
    sectionRoot.querySelectorAll("[data-bsv4-tile] [data-bsv4-row-id]").forEach((n) => {
      const id = n.dataset.bsv4RowId;
      for (const file of state2.files) {
        const row = (file.rows || []).find((r) => r.id === id);
        if (row) {
          projectPrice(n, file, row);
          break;
        }
      }
    });
  }
  function fmtIn2(n) {
    const num = parseFloat(n);
    return Number.isFinite(num) ? String(parseFloat(num.toFixed(2))) : String(n);
  }
  function syncRowDom(node, file, row) {
    const wEl = node.querySelector("[data-bsv4-w]");
    const hEl = node.querySelector("[data-bsv4-h]");
    const qEl = node.querySelector("[data-bsv4-qty]");
    const fmt2 = (n) => {
      const x = parseFloat(n);
      return Number.isFinite(x) && x > 0 ? x.toFixed(2) : n;
    };
    if (wEl && !pendingInputs.has(wEl)) wEl.value = fmt2(row.w);
    if (hEl && !pendingInputs.has(hEl)) hEl.value = fmt2(row.h);
    if (qEl && !pendingInputs.has(qEl)) qEl.value = row.qty;
    const select = node.querySelector("[data-bsv4-popular]");
    if (select) {
      select.value = row.mode === "popular" && row.popularKey ? row.popularKey : "";
      for (const opt of select.options) {
        if (!opt.dataset.orig) continue;
        let label = opt.dataset.orig;
        if (cfg3.smartSizing && opt.value === select.value && row.mode === "popular" && row.w > 0 && row.h > 0) {
          const parts = opt.dataset.orig.split("-");
          const desc = parts.length > 1 ? ` -${parts.slice(1).join("-")}` : "";
          label = `${fmtIn2(row.w)}" x ${fmtIn2(row.h)}"${desc}`;
        }
        if (opt.textContent !== label) opt.textContent = label;
      }
    }
    const smart = node.querySelector("[data-bsv4-smart]");
    if (smart) {
      smart.hidden = !(cfg3.smartSizing && row.mode === "popular" && row.popularKey);
    }
    const removeBtn = node.querySelector("[data-bsv4-row-remove]");
    if (removeBtn) removeBtn.classList.toggle("is-lone-hidden", (file.rows || []).length <= 1);
    const unsetPopular = row.mode === "popular" && !row.popularKey;
    const ext = fileExt(file);
    const raster = ext === "png" || ext === "jpg" || ext === "jpeg";
    const dpiEl = node.querySelector("[data-bsv4-dpi]");
    if (dpiEl) {
      const warn = !unsetPopular && raster && file.pxW && file.pxH && dpiWarns(row.mode || "custom", file.pxW, file.pxH, row.w, row.h);
      dpiEl.hidden = !warn;
      if (warn) dpiEl.textContent = "At this size, artwork clarity may be compromised \u2014 consider a smaller size.";
    }
    projectPrice(node, file, row);
  }
  function projectPrice(node, file, row) {
    const priceEl = node.querySelector("[data-bsv4-row-price]");
    const totalEl = node.querySelector("[data-bsv4-row-total]");
    const discEl = node.querySelector("[data-bsv4-row-disc]");
    if (row.mode === "popular" && !row.popularKey) {
      if (priceEl) priceEl.innerHTML = "&mdash;";
      if (totalEl) totalEl.innerHTML = "&mdash;";
      if (discEl) discEl.textContent = "";
      return;
    }
    const pricing = getState().pricing;
    const rows = pricing && pricing.rows || [];
    let entry = rows.find((r) => r.rowId === row.id);
    if (!entry) {
      const mine = rows.filter((r) => r.fileId === file.id);
      const byIndex = mine[file.rows.indexOf(row)];
      if (byIndex && byIndex.rowId == null) entry = byIndex;
    }
    if (!entry) return;
    const struck = entry.off > 0 && entry.orig > entry.unit;
    if (priceEl && Number.isFinite(entry.unit)) {
      priceEl.textContent = "";
      if (struck && Number.isFinite(entry.orig)) {
        const was = document.createElement("s");
        was.className = "bsv4-row__was";
        was.textContent = `$${entry.orig.toFixed(2)}`;
        priceEl.appendChild(was);
      }
      priceEl.appendChild(document.createTextNode(`$${entry.unit.toFixed(2)}`));
      const ea = document.createElement("small");
      ea.textContent = " ea";
      priceEl.appendChild(ea);
    }
    if (totalEl && Number.isFinite(entry.total)) {
      totalEl.textContent = "";
      if (struck && Number.isFinite(entry.origTotal)) {
        const was = document.createElement("s");
        was.className = "bsv4-row__was";
        was.textContent = `$${entry.origTotal.toFixed(2)}`;
        totalEl.appendChild(was);
      }
      totalEl.appendChild(document.createTextNode(`$${entry.total.toFixed(2)}`));
    }
    if (discEl) discEl.textContent = entry.off > 0 ? `${entry.off}% off` : "";
  }
  function applyTabToNode(node, tab) {
    node.querySelectorAll("[data-bsv4-mode-custom]").forEach((el) => {
      el.hidden = tab !== "custom";
    });
    node.querySelectorAll("[data-bsv4-mode-popular]").forEach((el) => {
      el.hidden = tab !== "popular";
    });
  }
  function ensureModeRow(file, tab) {
    if ((file.rows || []).some((r) => (r.mode || "custom") === tab)) return null;
    const inst = instances[0];
    if (!inst) return null;
    let row;
    if (tab === "popular") {
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode: "popular",
        w: 0,
        h: 0,
        qty: inst.lim.minQty,
        variantId: null
      };
    } else {
      const d = fileDims(file);
      const max = effectiveMax(inst.lim, null);
      const clamped = clampToMax(d.w, d.h, max.maxW, max.maxH) || d;
      const variant = matchCustomVariant(cfg3.variants || [], clamped.w, clamped.h);
      row = {
        id: `r-${++rowSeq}`,
        fileId: file.id,
        mode: "custom",
        w: clamped.w,
        h: clamped.h,
        qty: inst.lim.minQty,
        variantId: variant ? variant.id : null
      };
    }
    file.rows.push(row);
    return row;
  }
  function setSizeTab(tab) {
    const primary = instances[0];
    if (tab === "popular" && !(primary && primary.settings && primary.settings.popularEnabled)) return;
    if (getState().sizeTab === tab) return;
    const state2 = getState();
    for (const file of state2.files) ensureModeRow(file, tab);
    update({ files: state2.files, sizeTab: tab });
    announce("rows-changed", {});
  }
  function findRow(target) {
    const node = target.closest("[data-bsv4-row-id]");
    if (!node) return {};
    const id = node.dataset.bsv4RowId;
    for (const file of getState().files) {
      const row = file.rows.find((r) => r.id === id);
      if (row) return { node, file, row };
    }
    return { node };
  }
  function onInput(e) {
    const { node, file, row } = findRow(e.target);
    if (!row) return;
    if (e.target.matches("[data-bsv4-w], [data-bsv4-h]")) {
      const axis = e.target.matches("[data-bsv4-w]") ? "w" : "h";
      pendingInputs.add(e.target);
      debounce(`${row.id}-${axis}`, 300, () => {
        pendingInputs.delete(e.target);
        applyDimensionEdit(node, file, row, e.target);
      });
    } else if (e.target.matches("[data-bsv4-qty]")) {
      pendingInputs.add(e.target);
      debounce(`${row.id}-q`, 300, () => {
        pendingInputs.delete(e.target);
        const inst = instanceFor(node) || instances[0];
        row.qty = Math.max(inst.lim.minQty, parseInt(e.target.value, 10) || inst.lim.minQty);
        e.target.value = row.qty;
        commit();
      });
    }
  }
  function applyDimensionEdit(node, file, row, editedInput) {
    const inst = instanceFor(node) || instances[0];
    const axis = editedInput.matches("[data-bsv4-w]") ? "w" : "h";
    const errEl = node.querySelector("[data-bsv4-row-err]");
    if (errEl) errEl.hidden = true;
    const cap = Number.isFinite(row.capW) || Number.isFinite(row.capH) ? { maxW: row.capW, maxH: row.capH } : null;
    const max = effectiveMax(inst.lim, cap);
    const a = fileAspect(file);
    const out = computeLockedDims(editedInput.value, axis, a.w, a.h, {
      minW: inst.lim.minW,
      maxW: max.maxW,
      minH: inst.lim.minH,
      maxH: max.maxH
    });
    if (!out) return;
    if (out.err && errEl) flagErr(errEl, out.err);
    row.w = out.w;
    row.h = out.h;
    const variant = matchCustomVariant(cfg3.variants || [], out.w, out.h);
    row.variantId = variant ? variant.id : null;
    syncRowDom(node, file, row);
    commit();
  }
  function onChange(e) {
    if (!e.target.matches("[data-bsv4-popular]")) return;
    const { node, file, row } = findRow(e.target);
    if (!row) return;
    const inst = instanceFor(node) || instances[0];
    const value = e.target.value;
    if (!value) return;
    const box = parsePopularDims(value);
    if (!box) return;
    row.mode = "popular";
    row.popularKey = value;
    const cap = popularCapFor(inst.settings && inst.settings.popular, box.w, box.h);
    if (cap) {
      row.capW = cap.maxW;
      row.capH = cap.maxH;
    } else {
      delete row.capW;
      delete row.capH;
    }
    const a = fileAspect(file);
    const fit = containFit(a.w, a.h, box.w, box.h);
    const max = effectiveMax(inst.lim, cap);
    const clamped = clampToMax(fit.w, fit.h, max.maxW, max.maxH) || fit;
    row.w = clamped.w;
    row.h = clamped.h;
    const popularVariant = (cfg3.variants || []).find((v) => v.isPopular && v.option1 === value);
    const pricingVariant = cfg3.smartSizing ? matchCustomVariant(cfg3.variants || [], row.w, row.h) || popularVariant : popularVariant;
    row.variantId = pricingVariant ? pricingVariant.id : null;
    syncRowDom(node, file, row);
    commit();
  }
  function onClick(e) {
    const tab = e.target.closest("[data-bsv4-size-tab]");
    if (tab) {
      setSizeTab(tab.dataset.bsv4SizeTab);
      return;
    }
    if (e.target.closest("[data-bsv4-goto-popular]")) {
      setSizeTab("popular");
      return;
    }
    const add = e.target.closest("[data-bsv4-add-size]");
    if (add) {
      const state2 = getState();
      const file2 = state2.files.find((f) => f.id === state2.activeFileId) || state2.files[0];
      if (file2) {
        const d = fileDims(file2);
        addRow(file2, { w: d.w, h: d.h }, void 0, instanceFor(add));
      }
      return;
    }
    const { node, file, row } = findRow(e.target);
    if (!row) return;
    if (node.classList.contains("is-disabled")) {
      const field = e.target.closest(".bsv4-row__field");
      setSizeTab(row.mode || "custom");
      const el = field && field.querySelector("input, select");
      if (el && !el.disabled) {
        el.focus();
        if (el.tagName === "INPUT") el.select();
      }
      return;
    }
    const step = e.target.closest("[data-bsv4-step]");
    if (step) {
      const [axis, dir] = step.dataset.bsv4Step.split(":");
      const delta = dir === "+" ? 1 : -1;
      if (axis === "q") {
        const inst = instanceFor(node) || instances[0];
        row.qty = Math.max(inst.lim.minQty, row.qty + delta);
        const qEl = node.querySelector("[data-bsv4-qty]");
        if (qEl) qEl.value = row.qty;
        commit();
      } else {
        const input = node.querySelector(axis === "w" ? "[data-bsv4-w]" : "[data-bsv4-h]");
        if (!input) return;
        input.value = (parseFloat(input.value || 0) + delta * 0.25).toFixed(2);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
    } else if (e.target.closest("[data-bsv4-row-remove]")) {
      file.rows = file.rows.filter((r) => r.id !== row.id);
      node.remove();
      const state2 = getState();
      if (!file.rows.length && state2.files.length === 1 && state2.files[0].id === file.id) {
        update({ files: [], status: "empty", mode: "single" });
        announce("rows-changed", { totalQty: 0 });
        return;
      }
      commit();
    }
  }
  function flagErr(el, msg) {
    el.textContent = msg;
    el.hidden = false;
    el.classList.remove("is-fading");
    clearTimeout(el.__bsv4ErrT);
    el.__bsv4ErrT = setTimeout(() => {
      el.classList.add("is-fading");
      el.__bsv4ErrT = setTimeout(() => {
        el.hidden = true;
        el.classList.remove("is-fading");
      }, 400);
    }, 1800);
  }
  function debounce(key, ms, fn) {
    clearTimeout(debounces.get(key));
    debounces.set(key, setTimeout(fn, ms));
  }
  function commit() {
    update({ files: [...getState().files] });
    announce("rows-changed", { totalQty: pageQty() });
  }
  function refitRowsToAspect(file) {
    const inst = instances[0];
    if (!inst) return;
    const a = fileAspect(file);
    for (const row of file.rows || []) {
      if (row.mode === "popular" && row.popularKey) {
        const box = parsePopularDims(row.popularKey);
        if (!box) continue;
        const cap = Number.isFinite(row.capW) || Number.isFinite(row.capH) ? { maxW: row.capW, maxH: row.capH } : null;
        const max = effectiveMax(inst.lim, cap);
        const fit = containFit(a.w, a.h, box.w, box.h);
        const clamped = clampToMax(fit.w, fit.h, max.maxW, max.maxH) || fit;
        row.w = clamped.w;
        row.h = clamped.h;
      } else {
        const out = computeLockedDims(row.w, "w", a.w, a.h, inst.lim);
        if (!out) continue;
        row.w = out.w;
        row.h = out.h;
        const v = matchCustomVariant(cfg3.variants || [], row.w, row.h);
        if (v) row.variantId = v.id;
      }
    }
  }
  function pageQty() {
    return getState().files.reduce(
      (sum, f) => sum + f.rows.reduce((s, r) => s + (r.qty || 0), 0),
      0
    );
  }

  // src/pricing.js
  var round2 = (n) => parseFloat((Number(n) || 0).toFixed(2));
  var cfg4;
  var els2;
  var tiers;
  var parsedTiers;
  var model;
  var run = 0;
  var lastWrite = null;
  var teardown = null;
  var viewAllOpen = false;
  var yieldToMain = () => typeof scheduler !== "undefined" && scheduler.yield ? scheduler.yield() : new Promise((r) => setTimeout(r, 0));
  function resolveUnit(row, variants) {
    const direct = parseFloat(row.price);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const list = Array.isArray(variants) ? variants : [];
    if (row.variantId != null) {
      const v = list.find((x) => x.id === row.variantId);
      if (v) return parseFloat(v.price) || 0;
    }
    for (const v of list) {
      if (!v.isPopular) continue;
      const dims = parsePopularDims(v.option1);
      if (dims && dims.w === row.w && dims.h === row.h) return parseFloat(v.price) || 0;
    }
    const custom = matchCustomVariant(list, row.w, row.h);
    return custom ? parseFloat(custom.price) || 0 : 0;
  }
  function deriveVariantTiers(variants) {
    if (!Array.isArray(variants) || !variants.length) return null;
    const out = [];
    for (const v of variants) {
      const label = String(v.option1 || "").trim();
      if (!/^\d+(?:\s*-\s*\d+|\s*\+)?(?:\s*pcs?)?$/i.test(label)) return null;
      const price = parseFloat(v.price);
      if (!Number.isFinite(price)) return null;
      out.push({ qty: label, price });
    }
    const base = out[0].price;
    return out.map((t) => ({
      qty: t.qty,
      pct: base > 0 ? Math.max(0, Math.round((1 - t.price / base) * 100)) : 0,
      price: t.price
    }));
  }
  function computePricing({ files, cartQty, tiers: tierEntries, variants, model: mdl, activeRowId: activeRowId2 }) {
    const parsed = parseTiers(tierEntries || []);
    const flat = [];
    for (const f of files || []) {
      for (const r of f.rows || []) flat.push({ fileId: f.id, row: r });
    }
    const pageQty2 = flat.reduce((s, e) => s + (e.row.qty || 0), 0);
    if (mdl === "per-design") {
      const rows2 = flat.map(({ fileId, row }) => {
        const t = tierFor(parsed, Math.max(row.qty || 0, 1));
        const unit = t ? round2(tierEntries[t.index - 1].price) : 0;
        return {
          fileId,
          rowId: row.id,
          w: row.w,
          h: row.h,
          qty: row.qty,
          unit,
          total: round2(unit * (row.qty || 0)),
          orig: unit,
          origTotal: round2(unit * (row.qty || 0)),
          off: 0
        };
      });
      const total2 = round2(rows2.reduce((s, r) => s + r.total, 0));
      const last = flat.length ? tierFor(parsed, Math.max(flat[flat.length - 1].row.qty || 0, 1)) : null;
      const rawTier2 = last ? { qty: tierEntries[last.index - 1].qty, pct: tierEntries[last.index - 1].pct || 0 } : null;
      return {
        pricing: { model: "per-design", rows: rows2, total: total2, tier: rawTier2 },
        pageQty: pageQty2,
        totalQty: pageQty2,
        activeTier: last,
        activeBase: null
      };
    }
    const totalQty = pageQty2 + (cartQty || 0);
    const active = flat.length ? tierFor(parsed, Math.max(totalQty, 1)) : null;
    const off = active ? active.off : 0;
    const rows = flat.map(({ fileId, row }) => {
      const orig = round2(resolveUnit(row, variants));
      const unit = round2(discounted(orig, off));
      return {
        fileId,
        rowId: row.id,
        w: row.w,
        h: row.h,
        qty: row.qty,
        unit,
        total: round2(unit * (row.qty || 0)),
        orig,
        origTotal: round2(orig * (row.qty || 0)),
        off
      };
    });
    const total = round2(rows.reduce((s, r) => s + r.total, 0));
    const rawTier = active ? { qty: tierEntries[active.index - 1].qty, pct: tierEntries[active.index - 1].pct || 0 } : null;
    return {
      pricing: { model: "cumulative", rows, total, tier: rawTier },
      pageQty: pageQty2,
      totalQty,
      activeTier: active,
      // Tier-table reference row = the line item the customer is EDITING
      // (Owen 2026-07-10, modal parity): the last-focused row wins; fall back
      // to the newest row (the old behavior) when nothing was touched or the
      // focused row was deleted.
      activeBase: flat.length ? resolveUnit(
        (activeRowId2 && flat.find((e) => e.row.id === activeRowId2) || flat[flat.length - 1]).row,
        variants
      ) : null
    };
  }
  var activeRowId = null;
  function initPricing(root3, config) {
    cfg4 = config || {};
    if (!root3.dataset.bsv4PricingFocusBound) {
      root3.dataset.bsv4PricingFocusBound = "1";
      root3.addEventListener("focusin", (e) => {
        const node = e.target.closest && e.target.closest("[data-bsv4-row-id]");
        if (!node || node.dataset.bsv4RowId === activeRowId) return;
        activeRowId = node.dataset.bsv4RowId;
        project();
      });
    }
    if (!cfg4.pricingTable) return;
    const block = root3 && root3.querySelector('[data-bsv4-block="price_table"]');
    if (!block || block.dataset.bsv4PricingInit === "1") return;
    block.dataset.bsv4PricingInit = "1";
    if (teardown) teardown();
    els2 = { lists: [], viewAlls: [] };
    const pageList = block.querySelector("[data-bsv4-tiers]");
    if (pageList) els2.lists.push(pageList);
    const railHost = root3.querySelector("[data-bsv4-pricing-rail]");
    if (railHost && pageList) {
      let railList = railHost.querySelector("[data-bsv4-tiers]");
      if (!railList) {
        const wrap = document.createElement("div");
        wrap.className = "bsv4-pricing";
        wrap.appendChild(pageList.cloneNode(true));
        railHost.appendChild(wrap);
        railList = wrap.querySelector("[data-bsv4-tiers]");
      }
      railHost.hidden = false;
      els2.lists.push(railList);
    }
    for (const list of els2.lists) {
      const existing = list.querySelector(".bsv4-pricing__va");
      if (existing) {
        els2.viewAlls.push(existing);
        continue;
      }
      const va = document.createElement("button");
      va.type = "button";
      va.className = "bsv4-pricing__va";
      va.hidden = true;
      va.innerHTML = '<span class="bsv4-pricing__va-chev" aria-hidden="true"></span><span class="bsv4-pricing__va-text">View All</span>';
      va.addEventListener("click", () => {
        viewAllOpen = !viewAllOpen;
        project();
      });
      const firstTier = list.querySelector("[data-bsv4-tier]");
      if (firstTier) firstTier.parentElement.insertBefore(va, firstTier);
      else list.appendChild(va);
      els2.viewAlls.push(va);
    }
    model = "cumulative";
    tiers = cfg4.tiers || [];
    if ((cfg4.pricingTable.tierSource || "settings") === "variants") {
      const host2 = block.querySelector(".bsv4-pricing") || block;
      host2.setAttribute("data-bsv4-tier-source", "variants");
      const derived = deriveVariantTiers(cfg4.variants);
      if (derived) {
        model = "per-design";
        tiers = derived;
        renderTierRows(derived);
      }
    }
    parsedTiers = parseTiers(tiers);
    const ctl = new AbortController();
    const unsub = subscribe((_state, patch) => {
      if (patch && patch.pricing === lastWrite && Object.keys(patch).length === 1) return;
      project();
    });
    document.addEventListener("bsv4:rows-changed", () => project(), { signal: ctl.signal });
    document.addEventListener(
      "bsv4:cart-qty-refreshed",
      (e) => {
        cfg4.cartQty = e.detail.count;
        project();
      },
      { signal: ctl.signal }
    );
    teardown = () => {
      unsub();
      ctl.abort();
    };
    project();
  }
  function renderTierRows(derived) {
    for (const list of els2.lists) renderTierRowsInto(list, derived);
  }
  function renderTierRowsInto(list, derived) {
    const wrap = list && list.querySelector("[data-bsv4-tier-rows]");
    if (!wrap) return;
    wrap.textContent = "";
    derived.forEach((t, i) => {
      const row = document.createElement("div");
      row.className = "bsv4-pricing__row";
      row.setAttribute("data-bsv4-tier", String(i + 1));
      row.setAttribute("role", "row");
      const qty = document.createElement("span");
      qty.className = "bsv4-pricing__qty";
      qty.setAttribute("role", "cell");
      qty.textContent = /pc/i.test(t.qty) ? t.qty : `${t.qty} pcs`;
      const price = document.createElement("span");
      price.className = "bsv4-pricing__price";
      price.setAttribute("role", "cell");
      const amt = document.createElement("b");
      amt.setAttribute("data-bsv4-tier-price", "");
      amt.textContent = "\u2014";
      const ea = document.createElement("span");
      ea.className = "bsv4-pricing__ea";
      ea.hidden = true;
      ea.textContent = "ea";
      price.append(amt, ea);
      const off = document.createElement("span");
      off.className = "bsv4-pricing__off";
      off.setAttribute("role", "cell");
      off.textContent = t.pct ? `${t.pct}% off` : "\xA0";
      const msg = document.createElement("span");
      msg.className = "bsv4-pricing__msg";
      msg.setAttribute("data-bsv4-tier-msg", "");
      msg.setAttribute("role", "cell");
      msg.hidden = true;
      row.append(qty, price, off, msg);
      wrap.appendChild(row);
    });
  }
  function setMsg(el, parts) {
    el.textContent = "";
    for (const p of parts) {
      if (typeof p === "string") {
        el.append(p);
      } else {
        const b = document.createElement("b");
        b.textContent = p.b;
        el.append(b);
      }
    }
  }
  async function project() {
    const runId = ++run;
    const state2 = getState();
    const cartQty = state2.cartQty || cfg4.cartQty || 0;
    const files = state2.files.map((f) => ({
      ...f,
      rows: (f.rows || []).filter((r) => r.w > 0)
    }));
    const result = computePricing({
      files,
      cartQty,
      tiers,
      variants: cfg4.variants,
      model,
      activeRowId
    });
    await yieldToMain();
    if (runId !== run) return;
    if (JSON.stringify(result.pricing) !== JSON.stringify(state2.pricing)) {
      lastWrite = result.pricing;
      update({ pricing: result.pricing });
      announce("price-changed", { total: result.pricing.total, tier: result.pricing.tier });
    }
    const tier = result.activeTier;
    let msgParts = null;
    if (result.pricing.rows.length && tier) {
      if (tier.nextOff === null) {
        msgParts = model === "per-design" ? [`You've reached the `, { b: "maximum discount" }, `!`] : [`You've reached the `, { b: "maximum cumulative discount" }, `!`];
      } else if (model === "per-design") {
        const lastQty = result.pricing.rows[result.pricing.rows.length - 1].qty || 0;
        msgParts = ["Add ", { b: String(tier.max - lastQty + 1) }, ` more to reach ${tier.nextOff}% off.`];
      } else {
        const need = unitsToNextTier(tier, result.pageQty, cartQty);
        msgParts = ["Add ", { b: String(need) }, ` of this design or your next upload to reach ${tier.nextOff}% off savings.`];
      }
    }
    for (let li = 0; li < els2.lists.length; li++) {
      const list = els2.lists[li];
      const viewAll = els2.viewAlls[li];
      const foldBelow = tier ? tier.index : 0;
      const foldable = foldBelow > 1;
      if (viewAll) {
        viewAll.hidden = !foldable;
        const txt = viewAll.querySelector(".bsv4-pricing__va-text");
        if (txt) txt.textContent = viewAllOpen ? "hide" : "View All";
        viewAll.classList.toggle("is-open", viewAllOpen);
      }
      const rows = list.querySelectorAll("[data-bsv4-tier]");
      rows.forEach((row, i) => {
        const t = parsedTiers[i];
        if (!t) return;
        row.hidden = foldable && !viewAllOpen && t.index < foldBelow;
        const active = !!tier && t.index === tier.index;
        row.classList.toggle("is-active", active);
        const cell = row.querySelector("[data-bsv4-tier-price]");
        if (cell) {
          let amt = null;
          if (model === "per-design") {
            amt = `$${round2(tiers[i].price).toFixed(2)}`;
          } else if (result.activeBase != null && result.activeBase > 0) {
            amt = `$${discounted(result.activeBase, t.off).toFixed(2)}`;
          }
          cell.textContent = amt || "\u2014";
          const ea = row.querySelector(".bsv4-pricing__ea");
          if (ea) ea.hidden = !amt;
        }
        const msgEl = row.querySelector("[data-bsv4-tier-msg]");
        if (msgEl) {
          if (active && msgParts) {
            setMsg(msgEl, msgParts);
            msgEl.hidden = false;
          } else {
            msgEl.hidden = true;
          }
        }
      });
    }
  }

  // src/cart.js
  var cfg5;
  var els3;
  var busy = false;
  function initCart(root3, config) {
    cfg5 = config;
    els3 = {
      root: root3,
      btn: root3.querySelector("[data-bsv4-checkout]"),
      err: root3.querySelector("[data-bsv4-cart-err]"),
      // ALL precut checkboxes (Builder Pro plan §D): the page CTA card AND the
      // modal rail card both carry data-bsv4-precut — every instance binds,
      // and the store projection keeps them in lockstep.
      precuts: [...root3.querySelectorAll("[data-bsv4-precut]")],
      notes: () => root3.querySelector('.bsv4-cta .nt-design-note textarea[name="properties[Design Notes]"]')
    };
    if (!els3.btn) return;
    els3.btnLabel = els3.btn.textContent;
    const rewards = root3.querySelector("[data-bsv4-rewards]");
    if (rewards) {
      const pct = parseFloat(rewards.dataset.bsv4TierPct) || 2;
      const amtEl = rewards.querySelector("[data-bsv4-reward-amt]");
      subscribe(() => {
        const total = getState().pricing?.total || 0;
        const amt = `$${(total * pct / 100).toFixed(2)}`;
        if (amtEl && amtEl.textContent !== amt) amtEl.textContent = amt;
      });
    }
    if (els3.precuts.length) {
      for (const cb of els3.precuts) {
        cb.addEventListener("change", () => update({ precut: cb.checked }));
      }
      subscribe((state2, patch) => {
        if (!("precut" in patch)) return;
        for (const cb of els3.precuts) {
          if (cb.checked !== state2.precut) cb.checked = state2.precut;
        }
      });
    }
    els3.btn.addEventListener("click", () => addToCart());
    setAddAnotherGate(maybeCommitThenIntake);
    const addAnother = root3.querySelector("[data-bsv4-add-another]");
    const addInput = root3.querySelector("[data-bsv4-add-input]");
    if (addAnother && addInput) {
      const mainInput = root3.querySelector("[data-bsv4-file-input]");
      if (mainInput && mainInput.accept) addInput.accept = mainInput.accept;
      const zoneReady = () => !busy && getState().status !== "uploading";
      addAnother.addEventListener("click", () => {
        if (zoneReady()) addInput.click();
      });
      addAnother.addEventListener("keydown", (e) => {
        if ((e.key === "Enter" || e.key === " ") && zoneReady()) {
          e.preventDefault();
          addInput.click();
        }
      });
      addInput.addEventListener("change", () => {
        const files = Array.from(addInput.files || []);
        addInput.value = "";
        if (files.length) addAnotherFlow(() => intake(files));
      });
      addAnother.addEventListener("dragover", (e) => {
        e.preventDefault();
        addAnother.classList.add("is-dragover");
      });
      addAnother.addEventListener("dragleave", () => addAnother.classList.remove("is-dragover"));
      addAnother.addEventListener("drop", (e) => {
        e.preventDefault();
        addAnother.classList.remove("is-dragover");
        const files = Array.from(e.dataTransfer && e.dataTransfer.files || []);
        if (files.length && zoneReady()) addAnotherFlow(() => intake(files));
      });
    }
  }
  async function addAnotherFlow(run2) {
    const ok = await addToCart({ stayAndReset: true });
    if (!ok) return;
    try {
      await run2();
    } catch (_) {
    }
  }
  function maybeCommitThenIntake({ files, entries }) {
    if (busy) return true;
    const state2 = getState();
    if (state2.status !== "uploaded" || state2.mode === "multi" || !state2.files.length) return false;
    addAnotherFlow(() => entries ? intakeFromUrls(entries) : intake(files));
    return true;
  }
  async function showCartConfirm(committed) {
    try {
      const c2 = await fetch("/cart.js", { cache: "no-store" }).then((r) => r.json());
      const n = c2 && Number(c2.item_count);
      if (Number.isFinite(n) && n > 0) {
        const setAll = (sel, fn) => document.querySelectorAll(sel).forEach(fn);
        const sn = String(n);
        setAll("[data-header-cart-count]", (elm) => {
          elm.textContent = sn;
          elm.setAttribute("data-count", sn);
        });
        if (typeof c2.total_price === "number") {
          const money = `$${(c2.total_price / 100).toFixed(2)}`;
          setAll("[data-header-cart-total]", (elm) => {
            elm.textContent = money;
          });
        }
      }
    } catch (_) {
    }
    const el = document.createElement("div");
    el.className = "bsv4-cartok";
    el.setAttribute("role", "status");
    const c = committed || {};
    const price = typeof c.total === "number" && c.total > 0 ? `$${c.total.toFixed(2)}` : "";
    const K = "bsv4-cartok__";
    el.innerHTML = `<div class="${K}head"><span class="${K}check" aria-hidden="true"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5"/></svg></span><strong>Added to cart</strong><button type="button" class="${K}x" aria-label="Close">&times;</button></div>` + (c.name ? `<div class="${K}body">${c.thumb ? `<img class="${K}thumb" src="${c.thumb}" alt="">` : ""}<span class="${K}meta"><strong class="${K}name"></strong><span class="${K}qty">Qty ${c.qty || 1}</span></span>${price ? `<strong class="${K}price">${price}</strong>` : ""}</div>` : "");
    const nameEl = el.querySelector(".bsv4-cartok__name");
    if (nameEl) nameEl.textContent = c.name || "";
    (els3.root || document.body).appendChild(el);
    requestAnimationFrame(() => el.classList.add("is-in"));
    const dismiss = () => {
      el.classList.remove("is-in");
      setTimeout(() => el.remove(), 300);
    };
    let timer = setTimeout(dismiss, 4e3);
    el.addEventListener("mouseenter", () => clearTimeout(timer));
    el.addEventListener("mouseleave", () => {
      clearTimeout(timer);
      timer = setTimeout(dismiss, 1500);
    });
    el.querySelector(`.${K}x`).addEventListener("click", () => {
      clearTimeout(timer);
      dismiss();
    });
  }
  async function addToCart(opts = {}) {
    if (busy) return false;
    if (getState().status === "uploading") return false;
    hideErr();
    const state2 = getState();
    const noteEl = els3.notes();
    const noteBox = noteEl && noteEl.closest(".nt-design-note");
    const noteOpen = !noteBox || noteBox.getAttribute("data-collapsed") === "false";
    const designNote = noteEl && noteOpen ? noteEl.value.trim() : "";
    const popupSource = readPopupAttribution();
    const pageTab = state2.sizeTab === "popular" ? "popular" : "custom";
    const fileTab = (f) => {
      if (state2.mode !== "multi") return pageTab;
      const pop = els3.root.querySelector(
        `[data-bsv4-tile="${f.id}"] [data-bsv4-tiletab][value="popular"]`
      );
      return pop && pop.checked ? "popular" : "custom";
    };
    const offending = state2.files.find(
      (f) => fileTab(f) === "popular" && (f.rows || []).some((r) => (r.mode || "custom") === "popular" && !(r.w > 0))
    );
    if (offending) {
      showErr("Please select a popular size.");
      const scope = state2.mode === "multi" && els3.root.querySelector(`[data-bsv4-tile="${offending.id}"]`) || els3.root;
      const sel = scope.querySelector("[data-bsv4-popular]");
      if (sel && !sel.value) sel.focus();
      return false;
    }
    const items = [];
    const skipped = [];
    for (const file of state2.files) {
      const note = (file.designNote || designNote || "").trim();
      for (const row of file.rows) {
        if (!Number.isFinite(row.variantId) || !Number.isFinite(row.qty) || row.qty < 1) {
          skipped.push(row.id);
          continue;
        }
        items.push(buildItem(file, row, { designNote: note, precut: state2.precut, popupSource }));
      }
    }
    if (!items.length) {
      showErr(skipped.length ? "Those sizes could not be added \u2014 pick a size and try again." : "Upload a design and pick a size first.");
      return false;
    }
    // Pre-cut add-on (Justin 2026-07-27): every transfer line already carries
    // properties.Precut = "Yes"; the charge itself is a separate $0.19 product,
    // quantity = total pieces submitted with pre-cut on. Shopify merges repeat
    // submissions into the one line, so the cart total tracks the whole order.
    if (state2.precut && cfg5.precutEnabled && cfg5.precutVariantId) {
      const precutVid = Number(cfg5.precutVariantId);
      const precutQty = items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
      if (Number.isFinite(precutVid) && precutVid > 0 && precutQty > 0) {
        items.push({ id: precutVid, quantity: precutQty, properties: { "_precut_addon": "Yes" } });
      }
    }
    busy = true;
    els3.btn.disabled = true;
    els3.btn.textContent = "Adding\u2026";
    try {
      const res = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, attributes: "" })
      });
      let data = null;
      try {
        data = await res.json();
      } catch (_) {
        throw new Error("cart responded with a non-JSON page");
      }
      if (!res.ok || data && data.status && data.status >= 400) {
        throw new Error(data && (data.description || data.message) || `cart error ${res.status}`);
      }
      fireGa4(popupSource);
      announce("added-to-cart", { items: items.length });
      if (opts.stayAndReset) {
        const committed = {
          name: state2.files[0] && state2.files[0].name || "Your design",
          // _cartImg is on every payload item (Gate-1 captures) — no fallback.
          thumb: items[0] && items[0].properties && items[0].properties["_cartImg"] || "",
          qty: items.reduce((n, it) => n + it.quantity, 0),
          // payload always sets quantity
          total: (state2.pricing?.rows || []).reduce((n, r) => n + (r.total || 0), 0) || null
        };
        await refreshCartQty();
        reset();
        showCartConfirm(committed);
        if (noteEl) noteEl.value = "";
        busy = false;
        els3.btn.disabled = false;
        els3.btn.textContent = els3.btnLabel || "Proceed to Checkout";
      } else {
        window.location.href = "/cart";
      }
      return true;
    } catch (err) {
      showErr("Something went wrong adding to your cart \u2014 please try again.");
      announce("cart-error", { error: String(err && err.message) });
      busy = false;
      els3.btn.disabled = false;
      els3.btn.textContent = els3.btnLabel || "Proceed to Checkout";
      return false;
    }
  }
  async function refreshCartQty() {
    try {
      const res = await fetch(`${location.pathname}?view=itemsInCart__cumulative`).then((r) => r.json());
      const count = res ? Number(res.count) : NaN;
      if (Number.isFinite(count)) {
        update({ cartQty: count });
        announce("cart-qty-refreshed", { count });
      }
    } catch (_) {
    }
  }
  function readPopupAttribution() {
    try {
      return sessionStorage.getItem("dtf_popup_attribution");
    } catch (_) {
      return null;
    }
  }
  function fireGa4(popupAttrib) {
    if (!popupAttrib) return;
    try {
      sessionStorage.removeItem("dtf_popup_attribution");
      const age = Date.now() - parseInt(popupAttrib, 10);
      if (age <= 3e5 && typeof window.gtag === "function") {
        window.gtag("event", "dtf_popup_add_to_cart", {
          popup_version: "v1",
          popup_environment: window.Shopify?.theme?.role === "main" ? "production" : "staging",
          device_type: window.innerWidth <= 767 ? "mobile" : "desktop",
          time_to_conversion_ms: age,
          popup_source: "dtf_popup"
        });
      }
    } catch (_) {
    }
  }
  function errEls() {
    return [els3.err, els3.root.querySelector("[data-bsv4-modal-err]")].filter(Boolean);
  }
  function showErr(msg) {
    for (const el of errEls()) {
      el.textContent = msg;
      el.hidden = false;
    }
  }
  function hideErr() {
    for (const el of errEls()) el.hidden = true;
  }

  // src/modal.js
  var cfg6;
  var els4;
  var announcedClosed = true;
  var docBound2 = false;
  function capState(fileCount, maxFiles) {
    const max = Number.isFinite(maxFiles) && maxFiles > 0 ? maxFiles : Infinity;
    return {
      atCap: fileCount >= max,
      remaining: max === Infinity ? Infinity : Math.max(0, max - fileCount)
    };
  }
  function initModal(root3, config) {
    cfg6 = config;
    const wrap = root3.querySelector('[data-bsv4-block="multi_upload"]');
    if (!wrap || !cfg6.modal) return;
    if (wrap.dataset.bsv4ModalBound === "1") return;
    wrap.dataset.bsv4ModalBound = "1";
    const dialog = wrap.querySelector("[data-bsv4-modal]");
    if (!dialog) return;
    els4 = {
      root: root3,
      wrap,
      dialog,
      tiles: dialog.querySelector("[data-bsv4-tiles]"),
      tileTemplate: dialog.querySelector("[data-bsv4-tile-template]"),
      subtotal: dialog.querySelector("[data-bsv4-modal-subtotal]"),
      savingsRow: dialog.querySelector("[data-bsv4-modal-savings]"),
      savingsAmt: dialog.querySelector("[data-bsv4-modal-savings-amt]"),
      total: dialog.querySelector("[data-bsv4-modal-total]"),
      addMore: dialog.querySelector("[data-bsv4-modal-addfile]"),
      capMsg: dialog.querySelector("[data-bsv4-modal-cap]"),
      checkout: dialog.querySelector("[data-bsv4-modal-checkout]"),
      err: dialog.querySelector("[data-bsv4-modal-err]"),
      // Shared page element this block cooperates with (established hosting
      // exception — the uploader owns it; we only click it):
      fileInput: root3.querySelector("[data-bsv4-file-input]")
    };
    if (cfg6.modal.footerCtaText && els4.checkout) {
      els4.checkout.textContent = cfg6.modal.footerCtaText;
    }
    if (els4.capMsg && Number.isFinite(cfg6.modal.maxFiles)) {
      els4.capMsg.textContent = `Maximum ${cfg6.modal.maxFiles} designs per order`;
    }
    const precutCard = dialog.querySelector("[data-bsv4-modal-precut]");
    if (precutCard && !cfg6.precutEnabled) precutCard.hidden = true;
    dialog.addEventListener("cancel", (e) => e.preventDefault());
    dialog.addEventListener("close", onDialogClosed);
    for (const btn of dialog.querySelectorAll("[data-bsv4-view]")) {
      btn.addEventListener("click", () => {
        const large = btn.dataset.bsv4View === "large";
        dialog.classList.toggle("bsv4-modal--large", large);
        for (const b of dialog.querySelectorAll("[data-bsv4-view]")) {
          b.classList.toggle("is-active", b === btn);
        }
      });
    }
    els4.lightbox = dialog.querySelector("[data-bsv4-lightbox]");
    els4.lightboxImg = dialog.querySelector("[data-bsv4-lightbox-img]");
    els4.lightbox?.addEventListener("click", () => {
      els4.lightbox.close();
      els4.lightboxImg.src = "";
    });
    if (els4.addMore) {
      if (!els4.fileInput) els4.addMore.hidden = true;
      const browse = () => {
        if (capState(getState().files.length, cfg6.modal.maxFiles).atCap) return;
        els4.fileInput.click();
      };
      els4.addMore.addEventListener("click", browse);
      els4.addMore.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          browse();
        }
      });
      els4.addMore.addEventListener("dragover", (e) => {
        e.preventDefault();
        els4.addMore.classList.add("is-dragover");
      });
      els4.addMore.addEventListener("dragleave", () => els4.addMore.classList.remove("is-dragover"));
      els4.addMore.addEventListener("drop", (e) => {
        e.preventDefault();
        els4.addMore.classList.remove("is-dragover");
        if (capState(getState().files.length, cfg6.modal.maxFiles).atCap) return;
        if (!els4.fileInput || !e.dataTransfer?.files?.length) return;
        els4.fileInput.files = e.dataTransfer.files;
        els4.fileInput.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
    els4.checkout?.addEventListener("click", () => addToCart());
    dialog.querySelector("[data-bsv4-modal-upcancel]")?.addEventListener("click", () => {
      window.resetLoading?.();
    });
    els4.tiles.addEventListener("click", onTileClick);
    els4.tiles.addEventListener("change", onTileTabChange);
    if (cfg6.modal.perFileNotes) els4.tiles.addEventListener("input", onNoteInput);
    subscribe(sync);
    if (!docBound2) {
      docBound2 = true;
      document.addEventListener("bsv4:upload-started", onUploadStarted);
    }
    sync();
  }
  function onUploadStarted(e) {
    if (!els4 || getState().mode !== "multi") return;
    addPendingTile(e.detail || {});
    if (!els4.dialog.open) openDialog();
  }
  function addPendingTile({ id, name, localUrl }) {
    if (!id || els4.tiles.querySelector(`[data-bsv4-pending="${id}"]`)) return;
    const tile = document.createElement("section");
    tile.className = "bsv4-tile bsv4-tile--pending";
    tile.setAttribute("data-bsv4-pending", id);
    tile.setAttribute("aria-busy", "true");
    tile.innerHTML = '<aside class="bsv4-tile__side"><figure class="bsv4-tile__hero"><img class="bsv4-tile__thumb" alt="" width="200" height="200"></figure></aside><div class="bsv4-tile__main"><p class="bsv4-tile__pending-name"></p><span class="bsv4-skel"></span><span class="bsv4-skel"></span><span class="bsv4-skel"></span></div>';
    const img = tile.querySelector("img");
    img.src = localUrl || NINJA_GIF;
    if (!localUrl) img.classList.add("bsv4-tile__thumb--pending");
    tile.querySelector("p").textContent = name || "Uploading\u2026";
    els4.tiles.appendChild(tile);
  }
  function clearPendingTiles() {
    for (const t of [...els4.tiles.querySelectorAll("[data-bsv4-pending]")]) t.remove();
  }
  function sync() {
    const files = getState().files;
    const mode = getState().mode;
    if (getState().status !== "uploading") clearPendingTiles();
    const pending = els4.tiles.querySelector("[data-bsv4-pending]") !== null;
    if (files.length && mode === "multi") {
      for (const tile of [...els4.tiles.querySelectorAll("[data-bsv4-tile]")]) {
        if (!files.some((f) => f.id === tile.dataset.bsv4Tile)) tile.remove();
      }
      for (const file of files) {
        const tileEl = els4.tiles.querySelector(`[data-bsv4-tile="${file.id}"]`);
        if (!tileEl) {
          buildTile(file);
          continue;
        }
        const thumb = tileEl.querySelector("[data-bsv4-tile-thumb]");
        if (thumb) swapImage(thumb, thumbUrl(file), tileEl.querySelector(".bsv4-tile__hero"));
      }
      if (!els4.dialog.open) openDialog();
      queueMicrotask(adoptTileRows);
    } else if (!files.length && !pending) {
      for (const tile of [...els4.tiles.querySelectorAll("[data-bsv4-tile]")]) tile.remove();
      if (els4.dialog.open) closeDialog();
    }
    refreshChrome(files);
  }
  function buildTile(file) {
    const tile = els4.tileTemplate.content.firstElementChild.cloneNode(true);
    tile.dataset.bsv4Tile = file.id;
    for (const radio of tile.querySelectorAll("[data-bsv4-tiletab]")) {
      radio.name = `bsv4-tt-${file.id}`;
    }
    const thumb = tile.querySelector("[data-bsv4-tile-thumb]");
    if (thumb) {
      thumb.alt = file.name || "";
      swapImage(thumb, thumbUrl(file), tile.querySelector(".bsv4-tile__hero"));
    }
    const name = tile.querySelector("[data-bsv4-tile-name]");
    if (name) name.textContent = file.name || "";
    const note = tile.querySelector("[data-bsv4-tile-note]");
    if (note) {
      if (cfg6.modal.perFileNotes) {
        if (file.designNote) {
          note.value = file.designNote;
          const open2 = tile.querySelector("[data-bsv4-tile-note-open]");
          if (open2) open2.checked = true;
        }
      } else {
        (note.closest(".bsv4-tile__note") || note).remove();
      }
    }
    const pend = els4.tiles.querySelector(`[data-bsv4-pending="${file.id}"]`);
    if (pend) pend.replaceWith(tile);
    else els4.tiles.appendChild(tile);
    const host2 = rowsHost(tile);
    for (const row of file.rows) {
      if (!host2) continue;
      const node = row.id ? els4.root.querySelector(`[data-bsv4-row-id="${row.id}"]`) : null;
      if (node) host2.appendChild(node);
      else {
        try {
          renderRow(file, row, host2);
        } catch (err) {
          console.warn("bsv4 modal: shared row component unavailable", err);
        }
      }
    }
    applyTileDim(tile);
    announce("tile-created", { id: file.id, fileId: file.id, tileEl: tile });
  }
  function rowsHost(tile) {
    return tile.querySelector("[data-bsv4-tile-rows]");
  }
  function tileTab(tile) {
    const pop = tile.querySelector('[data-bsv4-tiletab][value="popular"]');
    return pop && pop.checked ? "popular" : "custom";
  }
  function applyTileDim(tile) {
    const file = getState().files.find((f) => f.id === tile.dataset.bsv4Tile);
    if (!file) return;
    const tab = tileTab(tile);
    for (const n of tile.querySelectorAll("[data-bsv4-row-id]")) {
      const row = (file.rows || []).find((r) => r.id === n.dataset.bsv4RowId);
      const off = (row && row.mode || "custom") !== tab;
      n.classList.toggle("is-disabled", off);
      n.querySelectorAll("input, select, button").forEach((el) => {
        el.disabled = off;
      });
    }
  }
  function applyAllTileDims() {
    for (const tile of els4.tiles.querySelectorAll("[data-bsv4-tile]")) applyTileDim(tile);
  }
  function activateTileTab(tile, file, mode) {
    const radio = tile.querySelector(`[data-bsv4-tiletab][value="${mode}"]`);
    if (radio && !radio.checked) radio.checked = true;
    if (!(file.rows || []).some((r) => (r.mode || "custom") === mode)) {
      const w = parseFloat(file.widthIn ?? file.inW);
      const h = parseFloat(file.heightIn ?? file.inH);
      try {
        addRow(file, { w, h }, rowsHost(tile), void 0, mode);
      } catch (err) {
        console.warn("bsv4 modal: shared row component unavailable", err);
      }
    }
    applyTileDim(tile);
  }
  function adoptTileRows() {
    if (getState().mode !== "multi") return;
    for (const file of getState().files) {
      const tile = els4.tiles.querySelector(`[data-bsv4-tile="${file.id}"]`);
      if (!tile) continue;
      const host2 = rowsHost(tile);
      if (!host2) continue;
      for (const row of file.rows || []) {
        if (!row.id) continue;
        const node = els4.root.querySelector(`[data-bsv4-row-id="${row.id}"]`);
        if (node && !host2.contains(node)) host2.appendChild(node);
      }
    }
    applyAllTileDims();
  }
  function onTileClick(e) {
    const tile = e.target.closest("[data-bsv4-tile]");
    if (!tile) return;
    const fileId = tile.dataset.bsv4Tile;
    const file = getState().files.find((f) => f.id === fileId);
    if (!file) return;
    const dimmed = e.target.closest("[data-bsv4-row-id]");
    if (dimmed && dimmed.classList.contains("is-disabled")) {
      e.stopPropagation();
      const row = (file.rows || []).find((r) => r.id === dimmed.dataset.bsv4RowId);
      activateTileTab(tile, file, row && row.mode || "custom");
      const field = e.target.closest(".bsv4-row__field");
      const el = field && field.querySelector("input, select");
      if (el && !el.disabled) {
        el.focus();
        if (el.tagName === "INPUT") el.select();
      }
      return;
    }
    if (e.target.closest("[data-bsv4-tile-add]")) {
      const mode = tileTab(tile);
      const w = parseFloat(file.widthIn ?? file.inW);
      const h = parseFloat(file.heightIn ?? file.inH);
      try {
        addRow(file, { w, h }, rowsHost(tile), void 0, mode);
      } catch (err) {
        console.warn("bsv4 modal: shared row component unavailable", err);
      }
    } else if (e.target.closest("[data-bsv4-tile-goto-popular]")) {
      activateTileTab(tile, file, "popular");
    } else if (e.target.closest("[data-bsv4-tile-zoom]")) {
      if (els4.lightbox && els4.lightboxImg) {
        els4.lightboxImg.alt = file.name || "";
        swapImage(els4.lightboxImg, thumbUrl(file), els4.lightbox);
        els4.lightbox.showModal();
      }
    } else if (e.target.closest("[data-bsv4-tile-remove]")) {
      const files = getState().files.filter((f) => f.id !== fileId);
      if (!files.length) {
        reset();
      } else {
        update({ files });
      }
      announce("file-removed", { id: fileId });
      announce("rows-changed", {});
    }
  }
  function onTileTabChange(e) {
    if (e.target.matches("[data-bsv4-tile-note-open]")) {
      const tile2 = e.target.closest("[data-bsv4-tile]");
      const file2 = tile2 && getState().files.find((f) => f.id === tile2.dataset.bsv4Tile);
      if (!file2) return;
      const note = tile2.querySelector("[data-bsv4-tile-note]");
      file2.designNote = e.target.checked && note ? note.value.trim() : "";
      return;
    }
    if (!e.target.matches("[data-bsv4-tiletab]") || !e.target.checked) return;
    const tile = e.target.closest("[data-bsv4-tile]");
    const file = tile && getState().files.find((f) => f.id === tile.dataset.bsv4Tile);
    if (!file) return;
    activateTileTab(tile, file, e.target.value === "popular" ? "popular" : "custom");
  }
  function onNoteInput(e) {
    if (!e.target.matches("[data-bsv4-tile-note]")) return;
    const tile = e.target.closest("[data-bsv4-tile]");
    const file = tile && getState().files.find((f) => f.id === tile.dataset.bsv4Tile);
    if (file) file.designNote = e.target.value.trim();
  }
  function refreshChrome(files) {
    const cap = capState(files.length, cfg6.modal.maxFiles);
    if (els4.addMore && !els4.addMore.hidden) {
      els4.addMore.classList.toggle("is-capped", cap.atCap);
      els4.addMore.setAttribute("aria-disabled", cap.atCap ? "true" : "false");
    }
    if (els4.capMsg) els4.capMsg.hidden = !cap.atCap;
    const pricing = getState().pricing || {};
    const rows = Array.isArray(pricing.rows) ? pricing.rows : [];
    const hasRows = rows.length > 0;
    const total = pricing.total || 0;
    const origSum = rows.reduce((s, r) => s + (Number.isFinite(r.origTotal) ? r.origTotal : r.total || 0), 0);
    const savings = Math.max(0, origSum - total);
    if (els4.subtotal) els4.subtotal.textContent = hasRows ? `$${origSum.toFixed(2)}` : "\u2014";
    if (els4.total) els4.total.textContent = hasRows ? `$${total.toFixed(2)}` : "\u2014";
    if (els4.savingsRow) {
      const show = hasRows && savings > 4e-3;
      els4.savingsRow.hidden = !show;
      if (show && els4.savingsAmt) els4.savingsAmt.textContent = `\u2212$${savings.toFixed(2)}`;
    }
  }
  function thumbUrl(file) {
    try {
      return previewUrl(file);
    } catch (_) {
      return file.imgixUrl || "";
    }
  }
  function openDialog() {
    if (!els4.dialog.open) {
      els4.dialog.show();
      announcedClosed = false;
      announce("modal-opened", { files: getState().files.length });
      els4.dialog.scrollIntoView({ block: "start" });
    }
  }
  function closeDialog() {
    if (els4.dialog.open) els4.dialog.close();
    onDialogClosed();
  }
  function onDialogClosed() {
    if (!announcedClosed) {
      announcedClosed = true;
      announce("dialog-closed", { files: getState().files.length });
    }
  }

  // src/designs.js
  var wired = false;
  function initDesigns(root3, config) {
    if (!root3.querySelector('[data-bsv4-panel="previous"] .md-container')) return;
    if (wired) return;
    wired = true;
    (function wireColsPicker() {
      const panel = root3.querySelector('[data-bsv4-panel="previous"]');
      if (!panel || panel.querySelector(".dtfut__cols") === null) return;
      const syncActiveCol = (cols) => {
        for (const b of panel.querySelectorAll(".dtfut__cols-btn")) {
          b.classList.toggle("is-active", b.getAttribute("data-dtfut-cols") === cols);
        }
      };
      const isMobile = window.matchMedia && window.matchMedia("(max-width: 749px)").matches;
      const initialCols = isMobile ? "2" : "4";
      panel.setAttribute("data-cols", initialCols);
      syncActiveCol(initialCols);
      const mobileCols = panel.querySelector(".dtfut__cols--mobile");
      const filtersRow = panel.querySelector(".md-filters__row");
      if (mobileCols && filtersRow && !filtersRow.contains(mobileCols)) {
        filtersRow.appendChild(mobileCols);
      }
      panel.addEventListener("click", (e) => {
        const btn = e.target.closest(".dtfut__cols-btn");
        if (!btn) return;
        const cols = btn.getAttribute("data-dtfut-cols");
        if (!cols) return;
        panel.setAttribute("data-cols", cols);
        syncActiveCol(cols);
      });
    })();
    (function wireNoResults() {
      const panel = root3.querySelector('[data-bsv4-panel="previous"]');
      const search = panel && panel.querySelector(".md-search-input");
      const grid = panel && panel.querySelector(".md-grid");
      if (!panel || !search || !grid) return;
      const refresh = () => {
        const hasQuery = search.value.trim().length > 0;
        const hasCard = grid.querySelector(".md-card") !== null;
        panel.setAttribute("data-dtfut-empty-search", hasQuery && !hasCard ? "true" : "false");
      };
      search.addEventListener("input", refresh);
      new MutationObserver(refresh).observe(grid, { childList: true });
      refresh();
    })();
    (function wireZeroDesignsHide() {
      const apply = (count) => {
        // Zero-designs hide DISABLED (Justin 2026-07-24): the Previous Designs
        // tab must always show next to Upload, even with no saved designs yet
        // (NOD designs API pending). Panel shows its own empty/login state.
        const pill = root3.querySelector('[data-bsv4-source="previous"]');
        if (pill) pill.hidden = false;
        for (const link of root3.querySelectorAll('[data-bsv4-addlink="previous"]')) {
          link.hidden = false;
        }
      };
      window.addEventListener("nt-md-count", (e) => {
        if (e.detail && typeof e.detail.count === "number") apply(e.detail.count);
      });
      if (document.body.getAttribute("data-md-has-designs") === "0") apply(0);
    })();
    document.addEventListener("bsv4:source-changed", (e) => {
      if (!e.detail || e.detail.source !== "previous") return;
      const md = document.querySelector('[data-bsv4-panel="previous"] .md-container');
      if (md && typeof md.__mdRenderNow === "function") {
        md.__mdRenderNow();
        announce("designs-opened", {});
      }
    });
    document.addEventListener("md-picker:use-designs", (e) => {
      if (window.__bsv4StudioPick || window.__studioPickActive) return;
      const items = e.detail && e.detail.items || [];
      const entries = items.map((it) => {
        const url = String(it.file || "").split("?")[0];
        return url ? {
          url,
          name: it.fileName || url.split("/").pop(),
          orderName: it.orderName || void 0,
          // AI multi-select dispatches isAIImage:true through this same
          // event (ai-chat-sandbox.js:1253-1260) — carry the attribution
          // flags so cart-payload stamps them (audit 2026-07-09).
          isAIImage: it.isAIImage || void 0,
          isAIEdit: it.isAIEdit || void 0,
          isReadyToPress: it.isReadyToPress || void 0
        } : null;
      }).filter(Boolean);
      if (!entries.length) return;
      if (maybeCommitThenIntake({ entries })) return;
      setSource("upload");
      intakeFromUrls(entries);
    });
  }

  // src/addmore-popups.js
  var wired2 = false;
  var openRef = null;
  function openPicker(mode) {
    if (openRef) openRef(mode);
  }
  function initAddmorePopups(root3) {
    if (wired2) return;
    const dlg = root3.querySelector("[data-bsv4-pickdlg]");
    if (!dlg || typeof dlg.showModal !== "function") {
      for (const link of root3.querySelectorAll("[data-bsv4-addlink]")) link.hidden = true;
      return;
    }
    wired2 = true;
    if (dlg.parentElement !== root3) root3.appendChild(dlg);
    const host2 = dlg.querySelector("[data-bsv4-pickdlg-host]");
    let adopted = null;
    (function ensureModalLinks() {
      const zone = root3.querySelector("[data-bsv4-modal-addfile]");
      if (!zone || zone.querySelector("[data-bsv4-addlink]")) return;
      const p = document.createElement("p");
      p.className = "bsv4-cta__addmore-links";
      for (const [mode, label] of [
        ["previous", "Choose a previously uploaded design"],
        ["ai", "Create with AI"]
      ]) {
        const b = document.createElement("button");
        b.type = "button";
        b.setAttribute("data-bsv4-addlink", mode);
        b.textContent = label;
        if (mode === "ai") {
          const or = document.createElement("span");
          or.textContent = " or ";
          p.appendChild(or);
        }
        p.appendChild(b);
      }
      const sub = zone.querySelector(".bsv4-cta__addmore-sub");
      if (sub && sub.parentNode === zone) sub.after(p);
      else zone.appendChild(p);
    })();
    for (const [mode, sel] of [
      ["previous", '[data-bsv4-panel="previous"]'],
      ["ai", '[data-bsv4-panel="ai"]']
    ]) {
      if (!root3.querySelector(sel)) {
        for (const link of root3.querySelectorAll(`[data-bsv4-addlink="${mode}"]`)) {
          link.hidden = true;
        }
      }
    }
    function open2(mode) {
      const node = root3.querySelector(`[data-bsv4-panel="${mode === "ai" ? "ai" : "previous"}"]`);
      if (!node || adopted) return;
      adopted = { node, parent: node.parentNode, next: node.nextSibling };
      host2.appendChild(node);
      node.hidden = false;
      dlg.setAttribute("aria-label", mode === "ai" ? "Create a design with AI" : "Your previous designs");
      dlg.showModal();
      if (mode === "previous") {
        const md = node.querySelector(".md-container");
        if (md && typeof md.__mdRenderNow === "function") md.__mdRenderNow();
        announce("designs-opened", { via: "add-another" });
      } else {
        injectAiAssets(node);
        document.dispatchEvent(
          new CustomEvent("dtfut:tab-change", { detail: { name: "ai" }, bubbles: true })
        );
      }
    }
    function restore() {
      if (!adopted) return;
      const { node, parent, next } = adopted;
      node.hidden = true;
      if (parent) parent.insertBefore(node, next);
      adopted = null;
      const zoom = dlg.querySelector("#ai-pop-zoom-modal");
      if (zoom) document.body.appendChild(zoom);
      window.__studioPickActive = false;
      window.__bsv4StudioPick = false;
    }
    function injectAiAssets(panel) {
      for (const attr of ["data-bsv4-ai-css", "data-bsv4-ai-js"]) {
        const url = panel.getAttribute(attr);
        if (!url) continue;
        if (attr === "data-bsv4-ai-css") {
          if (!document.querySelector(`link[href="${url}"]`)) {
            const l = document.createElement("link");
            l.rel = "stylesheet";
            l.href = url;
            document.head.appendChild(l);
          }
        } else if (!document.querySelector(`script[src="${url}"]`)) {
          const s = document.createElement("script");
          s.src = url;
          document.head.appendChild(s);
        }
      }
    }
    document.addEventListener(
      "click",
      (e) => {
        const link = e.target.closest && e.target.closest("[data-bsv4-addlink]");
        if (link && root3.contains(link)) {
          e.preventDefault();
          e.stopPropagation();
          open2(link.dataset.bsv4Addlink);
          return;
        }
        if (dlg.open && e.target === dlg) dlg.close();
        if (dlg.open) {
          const zoom = document.getElementById("ai-pop-zoom-modal");
          if (zoom && !dlg.contains(zoom)) dlg.appendChild(zoom);
        }
      },
      true
    );
    dlg.addEventListener("close", restore);
    openRef = open2;
    const closeBtn = dlg.querySelector("[data-bsv4-pickdlg-close]");
    if (closeBtn) closeBtn.addEventListener("click", () => dlg.close());
    document.addEventListener("md-picker:use-designs", () => {
      if (dlg.open) dlg.close();
    });
    document.addEventListener("bsv4:files-received", () => {
      if (dlg.open) dlg.close();
    });
    document.addEventListener("bsv4:added-to-cart", () => {
      if (dlg.open) dlg.close();
    });
  }

  // src/bgr.js
  var root;
  var bgrModal = null;
  var modalFile = null;
  var modalPending = null;
  function inTile(host2) {
    return !!(host2 && host2.closest("[data-bsv4-tile]"));
  }
  function initBgr(rootEl) {
    root = rootEl;
    ensureBgrModal();
    document.addEventListener("bsv4:file-uploaded", (e) => {
      const file = getState().files.find((f) => f.id === e.detail.id);
      if (file) renderControls(file);
    });
    document.addEventListener("bsv4:tile-created", (e) => {
      const file = getState().files.find((f) => f.id === e.detail.id);
      if (file) renderControls(file);
    });
    document.addEventListener("bsv4:file-removed", () => {
      const next = activeFile2();
      if (next) renderControls(next);
    });
    root.addEventListener("change", onToggle);
    root.addEventListener("click", onPick);
    subscribe(() => {
      const file = activeFile2();
      if (file) renderControls(file);
      for (const f of getState().files) {
        if (f.bgRemove && f.imgixUrl && f.bgrRun !== f.imgixUrl) {
          if (!f.bgrModel) f.bgrModel = "imgix";
          startCandidates(f);
          paintOptions(f);
        }
      }
    });
  }
  function activeFile2() {
    const s = getState();
    return s.files.find((f) => f.id === s.activeFileId) || s.files[0] || null;
  }
  function hostFor(file) {
    return root.querySelector(`[data-bsv4-tile="${file.id}"] [data-bsv4-bgr-host]`) || (activeFile2()?.id === file.id ? root.querySelector(".bsv4-preview [data-bsv4-bgr-host]") : null);
  }
  function renderControls(file) {
    const host2 = hostFor(file);
    if (!host2 || host2.dataset.bsv4BgrFor === file.id) return;
    host2.dataset.bsv4BgrFor = file.id;
    const p = file.params || {};
    const tile = inTile(host2);
    const bgrPanel = p.bgRemover && !tile ? `
      <div class="bsv4-bgr" data-bsv4-bgr-picker hidden>
        <div class="bsv4-bgr__head" aria-hidden="true">
          <span class="bsv4-bgr__lead">
            <span class="bsv4-bgr__new">NEW</span>
            <span class="bsv4-bgr__title">3 BG Removal options</span>
          </span>
          <button type="button" class="bsv4-bgr__collapse" data-bsv4-bgr-collapse aria-label="Hide background-removal options">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="bsv4-bgr__row" data-bsv4-bgr-row></div>
      </div>` : "";
    host2.innerHTML = `
    ${bgrPanel}
    <div class="bsv4-ai__bar">
      <span class="bsv4-ai-badge" aria-hidden="true"><svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M12 2l1.7 5.6L19 9l-5.3 1.4L12 16l-1.7-5.6L5 9l5.3-1.4z"/></svg><span>Powered by AI</span></span>
      ${p.bgRemover ? `
        <div class="bsv4-ai-card bsv4-ai-card--bgr">
          <label class="bsv4-toggle">
            <input type="checkbox" data-bsv4-toggle-bgr ${file.bgRemove ? "checked" : ""}>
            <span class="bsv4-toggle__text">Remove background</span>
            <span class="bsv4-toggle__switch" aria-hidden="true"><i></i></span>
          </label>${tile ? `
          <button type="button" class="bsv4-bgr__viewopts" data-bsv4-bgr-viewopts>View options</button>` : ""}
        </div>` : ""}
      ${p.superRes ? `
        <div class="bsv4-ai-card bsv4-ai-card--up">
          <label class="bsv4-toggle">
            <input type="checkbox" data-bsv4-toggle-sr ${file.superRes ? "checked" : ""}>
            <span class="bsv4-toggle__text">Upscale to 300 dpi</span>
            <span class="bsv4-toggle__switch" aria-hidden="true"><i></i></span>
          </label>
        </div>` : ""}
      <span class="bsv4-edit" data-bsv4-edit-host></span>
    </div>`;
    if (file.bgRemove) openPicker2(file);
  }
  function fileForTarget(target) {
    const host2 = target.closest("[data-bsv4-bgr-host]");
    if (!host2) return null;
    return getState().files.find((f) => f.id === host2.dataset.bsv4BgrFor) || null;
  }
  function onToggle(e) {
    if (e.target.matches("[data-bsv4-toggle-bgr]")) {
      const file = fileForTarget(e.target);
      if (!file) return;
      file.bgRemove = e.target.checked;
      if (file.bgRemove) openPicker2(file);
      else {
        const picker = hostFor(file)?.querySelector("[data-bsv4-bgr-picker]");
        if (picker) picker.hidden = true;
        if (bgrModal?.open && modalFile === file) bgrModal.close();
        file.bgrModel = null;
        commit2(file);
      }
    } else if (e.target.matches("[data-bsv4-toggle-sr]")) {
      const file = fileForTarget(e.target);
      if (!file) return;
      file.superRes = e.target.checked;
      commit2(file);
    }
  }
  function openPicker2(file) {
    if (!file.bgrModel) file.bgrModel = "imgix";
    if (file.bgrRun !== file.imgixUrl) startCandidates(file);
    const picker = hostFor(file)?.querySelector("[data-bsv4-bgr-picker]");
    if (picker) {
      picker.hidden = false;
      picker.classList.toggle("is-collapsed", !!file.bgrCollapsed);
      paintOptions(file);
    }
    commit2(file);
  }
  function startCandidates(file) {
    const runUrl = file.imgixUrl;
    file.bgrRun = runUrl;
    file.bgrVersions = {};
    file.bgrDisplay = {};
    file.bgrVisible = null;
    file.bgrPending = { imgix: true, ideogram: true, pixelcut: true };
    const imgixCand = candidateUrl(runUrl, BGR_MODELS[0]);
    const bare = runUrl.split("?")[0];
    const paintAll = () => {
      if (file.bgRemove) paintOptions(file);
      if (bgrModal?.open && modalFile === file) paintModalOpts(file);
    };
    const engine = window.__bsv4BgrRun;
    if (engine) {
      engine(bare, imgixCand, null, (cand) => {
        if (file.bgrRun !== runUrl) return;
        delete file.bgrPending[cand.name];
        file.bgrVersions[cand.name] = cand.persistentUrl;
        file.bgrDisplay[cand.name] = cand.url || cand.persistentUrl;
        paintAll();
        if (file.bgRemove && cand.name === file.bgrModel) commit2(file);
      }).then((res) => {
        if (file.bgrRun !== runUrl) return;
        file.bgrPending = {};
        file.bgrVisible = res.all.map((c) => c.name);
        paintAll();
        announce("bgr-ready", { id: file.id, models: Object.keys(file.bgrVersions) });
        if (file.bgRemove) commit2(file);
      }).catch(() => {
        if (file.bgrRun !== runUrl) return;
        file.bgrPending = {};
        file.bgrVersions.imgix = imgixCand;
        file.bgrVisible = ["imgix"];
        paintAll();
        commit2(file);
      });
      return;
    }
    file.bgrPending = {};
    file.bgrVersions.imgix = imgixCand;
    file.bgrVisible = ["imgix"];
    paintAll();
    commit2(file);
  }
  function paintSlots(container, file, pickAttr, selectedKey) {
    if (!container) return;
    const versions = file.bgrVersions || {};
    const pending = file.bgrPending || {};
    const keys = file.bgrVisible || BGR_MODELS.map((m) => m.key);
    const paintKey = file.id + "|" + keys.join(",");
    if (container.dataset.bsv4BgrPaintFor !== paintKey) {
      container.dataset.bsv4BgrPaintFor = paintKey;
      container.innerHTML = keys.map((k) => `<span style="display:contents" data-bsv4-bgr-slot="${k}"></span>`).join("");
    }
    keys.forEach((key, i) => {
      const m = BGR_MODELS.find((x) => x.key === key) || { key };
      const slot = container.querySelector(`[data-bsv4-bgr-slot="${m.key}"]`);
      if (!slot) return;
      const display = file.bgrDisplay && file.bgrDisplay[m.key] || versions[m.key];
      const state2 = versions[m.key] ? `done:${display}` : pending[m.key] ? "pending" : "failed";
      if (slot.dataset.state !== state2) {
        slot.dataset.state = state2;
        if (state2 === "pending") {
          slot.innerHTML = `
        <span class="bsv4-bgr__tab is-loading">
          <span class="bsv4-bgr__thumbwrap bsv4-bgr__thumbwrap--loading"><span class="bsv4-bgr__spin" aria-hidden="true"></span></span>
          <span class="bsv4-bgr__label">Option ${i + 1}</span>
        </span>`;
        } else if (state2 === "failed") {
          slot.innerHTML = "";
        } else {
          slot.innerHTML = `
        <button type="button" class="bsv4-bgr__tab" ${pickAttr}="${m.key}">
          <span class="bsv4-bgr__thumbwrap"><img class="bsv4-bgr__thumb" src="${display}" alt="Option ${i + 1}" loading="lazy"></span>
          <span class="bsv4-bgr__label">Option ${i + 1}</span>
        </button>`;
          const img = slot.querySelector("img");
          const bare = `${(file.imgixUrl || "").split("?")[0]}?trim=colorUnlessAlpha`;
          img.onerror = () => {
            img.onerror = null;
            if (bare && img.src !== bare) img.src = bare;
          };
        }
      }
      const btn = slot.querySelector("button");
      if (btn) btn.classList.toggle("is-active", selectedKey === m.key);
    });
  }
  function paintOptions(file) {
    const row = hostFor(file)?.querySelector("[data-bsv4-bgr-row]");
    paintSlots(row, file, "data-bsv4-bgr-model", file.bgrModel);
  }
  function onPick(e) {
    const view = e.target.closest("[data-bsv4-bgr-viewopts]");
    if (view) {
      const file2 = fileForTarget(view);
      if (file2) openBgrModal(file2);
      return;
    }
    const chevron = e.target.closest("[data-bsv4-bgr-collapse]");
    if (chevron) {
      const file2 = fileForTarget(chevron);
      if (!file2) return;
      file2.bgrCollapsed = !file2.bgrCollapsed;
      const picker = hostFor(file2)?.querySelector("[data-bsv4-bgr-picker]");
      if (picker) picker.classList.toggle("is-collapsed", file2.bgrCollapsed);
      return;
    }
    const btn = e.target.closest("[data-bsv4-bgr-model]");
    if (!btn) return;
    const file = fileForTarget(btn);
    if (!file) return;
    file.bgrModel = btn.dataset.bsv4BgrModel;
    paintOptions(file);
    commit2(file);
  }
  function commit2(file) {
    syncDims(file);
    update({ files: [...getState().files] });
    announce("bgr-changed", { id: file.id, on: file.bgRemove, model: file.bgrModel });
  }
  async function alphaTrim(url) {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const W = Math.min(1200, img.width);
    const H = Math.max(1, Math.round(img.height * (W / img.width)));
    const c = document.createElement("canvas");
    c.width = W;
    c.height = H;
    const x = c.getContext("2d");
    x.drawImage(img, 0, 0, W, H);
    const d = x.getImageData(0, 0, W, H).data;
    let t = H, b = -1, l = W, r = -1;
    for (let i = 0, n = W * H; i < n; i++) {
      if (d[i * 4 + 3] > 16) {
        const yy = i / W | 0, xx = i % W;
        if (yy < t) t = yy;
        if (yy > b) b = yy;
        if (xx < l) l = xx;
        if (xx > r) r = xx;
      }
    }
    if (b < 0) return null;
    const w = r - l + 1, h = b - t + 1;
    const oc = document.createElement("canvas");
    oc.width = w;
    oc.height = h;
    oc.getContext("2d").drawImage(c, l, t, w, h, 0, 0, w, h);
    return { url: oc.toDataURL("image/png"), w, h };
  }
  async function syncDims(file) {
    const chosen = file.bgRemove && file.bgrModel && file.bgrVersions?.[file.bgrModel];
    const sig = chosen ? file.bgrModel + "|" + chosen : "off";
    if (file.bgrDsig === sig) return;
    file.bgrDsig = sig;
    if (!chosen) {
      if (!file.prePx) return;
      file.pxW = file.prePx.w;
      file.pxH = file.prePx.h;
      delete file.prePx;
    } else {
      try {
        const m = await alphaTrim(chosen);
        if (file.bgrDsig !== sig) return;
        if (!m) throw 0;
        file.bgrDisplay[file.bgrModel] = m.url;
        paintOptions(file);
        if (bgrModal?.open && modalFile === file) paintModalOpts(file);
        if (!file.prePx) file.prePx = { w: file.pxW, h: file.pxH };
        file.pxW = file.prePx.w || m.w;
        file.pxH = Math.round(file.pxW * (m.h / m.w));
      } catch (_) {
        setTimeout(() => {
          if (file.bgrDsig === sig) {
            file.bgrDsig = null;
            syncDims(file);
          }
        }, 4e3);
        return;
      }
    }
    file.inW = (file.pxW / 300).toFixed(2);
    file.inH = (file.pxH / 300).toFixed(2);
    refitRowsToAspect(file);
    update({ files: [...getState().files] });
    announce("rows-changed", {});
  }
  function ensureBgrModal() {
    if (bgrModal) return;
    bgrModal = document.createElement("dialog");
    bgrModal.className = "bsv4-bgr-modal";
    bgrModal.setAttribute("data-bsv4-bgr-modal", "");
    bgrModal.setAttribute("aria-label", "Background removal options");
    bgrModal.innerHTML = `
    <div class="bsv4-bgr-modal__card">
      <header class="bsv4-bgr-modal__head">
        <span class="bsv4-bgr__new">NEW</span>
        <h3 class="bsv4-bgr-modal__title">3 BG Removal options</h3>
        <button type="button" class="bsv4-bgr-modal__x" data-bsv4-bgr-cancel aria-label="Close">&times;</button>
      </header>
      <div class="bsv4-bgr-modal__opts" data-bsv4-bgr-modal-opts></div>
      <footer class="bsv4-bgr-modal__foot">
        <button type="button" class="bsv4-bgr-modal__cancel" data-bsv4-bgr-cancel>Cancel</button>
        <button type="button" class="bsv4-btn bsv4-btn--primary bsv4-bgr-modal__apply" data-bsv4-bgr-apply>Apply</button>
      </footer>
    </div>`;
    root.appendChild(bgrModal);
    bgrModal.addEventListener("click", (e) => {
      if (e.target === bgrModal) {
        bgrModal.close();
        return;
      }
      const pick = e.target.closest("[data-bsv4-modelpick]");
      if (pick) {
        modalPending = pick.dataset.bsv4Modelpick;
        if (modalFile) paintModalOpts(modalFile);
        return;
      }
      if (e.target.closest("[data-bsv4-bgr-apply]")) {
        if (modalFile) {
          modalFile.bgrModel = modalPending;
          commit2(modalFile);
        }
        bgrModal.close();
        return;
      }
      if (e.target.closest("[data-bsv4-bgr-cancel]")) bgrModal.close();
    });
    bgrModal.addEventListener("close", () => {
      modalFile = null;
      modalPending = null;
    });
  }
  function openBgrModal(file) {
    modalFile = file;
    modalPending = file.bgrModel || "imgix";
    paintModalOpts(file);
    if (!bgrModal.open) bgrModal.showModal();
  }
  function paintModalOpts(file) {
    const box = bgrModal?.querySelector("[data-bsv4-bgr-modal-opts]");
    paintSlots(box, file, "data-bsv4-modelpick", modalPending);
  }

  // src/handoffs.js
  var STASH_KEY = "dtf:pending-ai-uploads:v1";
  function initHandoffs() {
    rehydrateStash();
    consumeParams();
  }
  function rehydrateStash() {
    try {
      const raw = sessionStorage.getItem(STASH_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const items = parsed && Array.isArray(parsed.items) ? parsed.items : null;
      if (!items || !items.length) return;
      sessionStorage.removeItem(STASH_KEY);
      const u = new URL(window.location.href);
      if (u.searchParams.get("file") || u.searchParams.get("my_designs_selection")) return;
      if (items.length === 1) {
        const url = items[0] && (items[0].file || items[0].url);
        if (!url) return;
        u.searchParams.set("file", url);
      } else {
        const payload = items.map((it, i) => ({
          id: `ai-stash-${Date.now()}-${i}`,
          name: it && it.fileName || `ai-image-${i + 1}.png`,
          fileURL: it && (it.file || it.url)
        })).filter((e) => !!e.fileURL);
        if (!payload.length) return;
        u.searchParams.set("my_designs_selection", JSON.stringify(payload));
      }
      window.location.replace(u.toString());
    } catch (_) {
    }
  }
  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }
  function dropParams(...names) {
    const u = new URL(window.location.href);
    for (const n of names) u.searchParams.delete(n);
    history.replaceState(null, "", u.toString());
  }
  async function consumeParams() {
    const ordertype = getParam("ordertype");
    if (ordertype) {
      setAttribution({ designType: 1, orderName: getParam("order_name") || "" });
    }
    const src = getParam("src");
    if (src === "dtf_popup") sessionStorage.setItem("dtf_popup_attribution", String(Date.now()));
    else if (src) sessionStorage.removeItem("dtf_popup_attribution");
    if (src) dropParams("src");
    const selRaw = getParam("my_designs_selection");
    if (selRaw) {
      dropParams("my_designs_selection");
      let sel = [];
      try {
        sel = JSON.parse(decodeURIComponent(selRaw));
      } catch (_) {
        sel = [];
      }
      if (Array.isArray(sel) && sel.length) {
        await intakeFromUrls(
          sel.map((it) => ({ url: it.fileURL, name: it.name, orderName: it.orderName }))
        );
        return;
      }
    }
    let fileURL = getParam("file");
    if (!fileURL) return;
    fileURL = fileURL.replace("#", "%23").split("?")[0];
    const flags = {};
    if (getParam("rtp") === "Y") flags.isReadyToPress = "Yes";
    if (getParam("_design_studio") === "Yes") flags.isDesignStudio = "Yes";
    dropParams("file", "rtp", "_design_studio", "order_name", "ordertype", "orderFileWidth", "orderFileHeight");
    if (fileURL.includes("amazonaws.com") || fileURL.includes("imgix.net")) {
      await intakeFromUrls([{ url: fileURL, ...flags }]);
    } else {
      try {
        const blob = await fetch(fileURL).then((r) => r.blob());
        const name = fileURL.split("/").pop() || "design.png";
        await intake([new File([blob], name, { type: blob.type || "image/png" })]);
      } catch (err) {
        console.warn("[bsv4] external file handoff failed:", err && err.message);
      }
    }
  }

  // src/integrations.js
  function initIntegrations(root3) {
    wireCountdown(root3);
    wireBackdropClose(root3);
    wireBfcacheResync(root3);
    wireReviewCommas(root3);
    document.addEventListener("bsv4:file-uploaded", () => window.refreshGRTUpsellBlocks?.());
  }
  function wireReviewCommas(root3) {
    const cell = root3.querySelector(".bsv4__meta-rating");
    if (!cell) return;
    const format = () => {
      const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);
      let node;
      while (node = walker.nextNode()) {
        const m = node.nodeValue.match(/\b(\d{4,})\b/);
        if (m) {
          const withCommas = Number(m[1]).toLocaleString("en-US");
          if (withCommas !== m[1]) node.nodeValue = node.nodeValue.replace(m[1], withCommas);
        }
      }
    };
    format();
    const mo = new MutationObserver(() => format());
    mo.observe(cell, { childList: true, characterData: true, subtree: true });
  }
  function wireBackdropClose(root3) {
    for (const dialog of root3.querySelectorAll("dialog")) {
      if (dialog.closest('[data-bsv4-block="multi_upload"]')) continue;
      dialog.addEventListener("click", (e) => {
        if (e.target !== dialog) return;
        dialog.close();
        document.dispatchEvent(new CustomEvent("bsv4:dialog-closed"));
      });
    }
  }
  function wireBfcacheResync(root3) {
    window.addEventListener("pageshow", async (e) => {
      if (!e.persisted) return;
      try {
        const res = await fetch(`${location.pathname}?view=itemsInCart__cumulative`).then((r) => r.json());
        const count = res ? Number(res.count) : NaN;
        if (Number.isFinite(count)) {
          root3.__bsv4cfg && (root3.__bsv4cfg.cartQty = count);
          update({ cartQty: count });
          document.dispatchEvent(new CustomEvent("bsv4:cart-qty-refreshed", { detail: { count } }));
        }
      } catch (_) {
      }
    });
  }
  function wireCountdown(root3) {
    const perk = root3.querySelector("[data-bsv4-countdown]");
    const textEl = perk && perk.querySelector("[data-bsv4-countdown-text]");
    if (!perk || !textEl || !textEl.textContent.includes("[")) return;
    const template = textEl.textContent;
    perk.hidden = true;
    const wire = (attempt = 0) => {
      const dateEl = [...document.querySelectorAll("date")].find((el) => el.textContent.trim());
      const timeEl = document.querySelector(".grt-time-box time") || document.querySelector("time");
      if (!dateEl && !timeEl) {
        if (attempt < 5) setTimeout(() => wire(attempt + 1), 1e3 * (attempt + 1));
        return;
      }
      const apply = () => {
        const rawDate = dateEl ? dateEl.textContent.trim() : "";
        const date = rawDate.includes(",") ? rawDate.split(",").slice(1).join(",").trim() : rawDate;
        const digits = timeEl ? timeEl.textContent.replace(/[A-Za-z]/g, "").replace(/\s+/g, "") : "";
        const parts = digits.split(":").filter(Boolean);
        const time = parts.length >= 2 ? `${parseInt(parts[0], 10)}h ${parseInt(parts[1], 10)}m` : "";
        perk.hidden = !date && !time;
        if (!perk.hidden) {
          textEl.innerHTML = template.replace("[DATE]", `<strong class="bsv4-perks__hl">${date}</strong>`).replace("[TIME]", `<strong class="bsv4-perks__hl">${time}</strong>`);
        }
      };
      apply();
      if (timeEl) {
        new MutationObserver(apply).observe(timeEl, { characterData: true, childList: true, subtree: true });
      }
    };
    wire();
  }

  // src/ga-bridge.js
  var UPLOAD_TAB_MAP = {
    upload: "upload",
    previous: "previous_designs",
    ai: "create_ai",
    studio: "design_studio"
  };
  var OPEN_SETTLE_MS = 250;
  var COMMIT_MS = 600;
  var carouselFired = false;
  var openFired = false;
  var proceeded = false;
  var openTimer = null;
  var lastCommitted = {};
  var commitTimers = {};
  function environment() {
    return window.Shopify && Shopify.theme && Shopify.theme.role === "main" ? "production" : "staging";
  }
  function fire(name, params) {
    if (typeof window.gtag === "undefined") return;
    params = params || {};
    params.environment = environment();
    params.device_type = window.innerWidth <= 767 ? "mobile" : "desktop";
    window.gtag("event", name, params);
  }
  function uploadIndex(el) {
    const tile = el.closest && el.closest("[data-bsv4-tile]");
    if (!tile || !tile.parentElement) return void 0;
    return [...tile.parentElement.children].indexOf(tile);
  }
  function inModal(el) {
    return !!(el.closest && el.closest("[data-bsv4-modal]"));
  }
  function initGaBridge(root3) {
    if (window.__bsv4GaBridge) return;
    window.__bsv4GaBridge = true;
    document.addEventListener(
      "click",
      (e) => {
        const t = e.target;
        if (!t || !t.closest || !root3.contains(t)) return;
        if (t.matches('input[type="file"]')) return;
        if (inModal(t)) {
          if (t.closest("[data-bsv4-modal-checkout]")) {
            proceeded = true;
            fire("multiupload_proceed_checkout", {});
          } else if (t.closest("[data-bsv4-tile-remove]")) {
            fire("multiupload_delete_upload", { upload_index: uploadIndex(t) });
          } else if (t.closest("[data-bsv4-edit-open]")) {
            fire("multiupload_edit_ai_click", { upload_index: uploadIndex(t) });
          } else if (t.closest(".bsv4-tile__add")) {
            fire("multiupload_add_size_click", { upload_index: uploadIndex(t) });
          }
          return;
        }
        const source = t.closest("[data-bsv4-source]");
        if (source) {
          const key = source.getAttribute("data-bsv4-source");
          fire("dtf_pdp_upload_tab_click", { tab: UPLOAD_TAB_MAP[key] || key });
          return;
        }
        if (!carouselFired && t.closest("[data-bsv4-gal-prev], [data-bsv4-gal-next], [data-bsv4-thumb]")) {
          carouselFired = true;
          fire("dtf_pdp_carousel_interact", {});
          return;
        }
        const addlink = t.closest("[data-bsv4-addlink]");
        if (addlink) {
          fire(
            addlink.getAttribute("data-bsv4-addlink") === "ai" ? "dtf_pdp_add_design_ai_click" : "dtf_pdp_add_design_previous_click",
            {}
          );
          return;
        }
        if (t.closest("[data-bsv4-add-another]")) {
          fire("dtf_pdp_add_design_upload_click", {});
          return;
        }
        if (t.closest(".nt-design-note__header")) {
          fire("dtf_pdp_note_toggle", {});
          return;
        }
        if (t.closest("[data-bsv4-checkout]")) {
          fire("dtf_pdp_proceed_checkout", {});
          return;
        }
        const sizeTab = t.closest("[data-bsv4-size-tab]");
        if (sizeTab) {
          fire("dtf_pdp_size_tab_switch", {
            tab: sizeTab.getAttribute("data-bsv4-size-tab")
          });
        }
      },
      true
    );
    document.addEventListener(
      "change",
      (e) => {
        const t = e.target;
        if (!t || !t.closest || !root3.contains(t)) return;
        if (!inModal(t)) {
          if (t.matches("[data-bsv4-precut]")) fire("dtf_pdp_precut_toggle", {});
          return;
        }
        if (t.matches("[data-bsv4-tiletab]")) {
          if (t.checked)
            fire("multiupload_size_tab_switch", {
              tab: t.value,
              upload_index: uploadIndex(t)
            });
        } else if (t.matches("[data-bsv4-popular]")) {
          if (t.value)
            fire("multiupload_popular_size_select", {
              size: t.value.trim(),
              upload_index: uploadIndex(t)
            });
        } else if (t.matches("[data-bsv4-toggle-bgr]")) {
          fire("multiupload_remove_bg_toggle", {
            state: t.checked ? "on" : "off",
            upload_index: uploadIndex(t)
          });
        } else if (t.matches("[data-bsv4-toggle-sr]")) {
          fire("multiupload_upscale_toggle", {
            state: t.checked ? "on" : "off",
            upload_index: uploadIndex(t)
          });
        } else if (t.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) {
          commitField(t);
        }
      },
      true
    );
    document.addEventListener(
      "input",
      (e) => {
        const t = e.target;
        if (!t || !t.closest || !root3.contains(t) || !inModal(t)) return;
        if (!t.matches("[data-bsv4-w], [data-bsv4-h], [data-bsv4-qty]")) return;
        const idx = uploadIndex(t);
        const key = (t.matches("[data-bsv4-qty]") ? "qty:" : "sz:") + idx + ":" + (t.matches("[data-bsv4-w]") ? "w" : "h");
        clearTimeout(commitTimers[key]);
        commitTimers[key] = setTimeout(() => commitField(t), COMMIT_MS);
      },
      true
    );
    function commitField(t) {
      const idx = uploadIndex(t);
      if (idx === void 0) return;
      if (t.matches("[data-bsv4-qty]")) {
        const qty = parseInt(t.value, 10);
        if (isNaN(qty) || lastCommitted["qty:" + idx] === qty) return;
        lastCommitted["qty:" + idx] = qty;
        fire("multiupload_qty_change", { qty, upload_index: idx });
      } else {
        const dim = t.matches("[data-bsv4-w]") ? "width" : "height";
        const num = parseFloat(t.value);
        const key = "sz:" + idx + ":" + dim;
        if (isNaN(num) || lastCommitted[key] === num) return;
        lastCommitted[key] = num;
        fire("multiupload_custom_size_edit", {
          dimension: dim,
          size_value: num,
          upload_index: idx
        });
      }
    }
    const dialog = root3.querySelector("[data-bsv4-modal]");
    if (dialog) {
      const onOpenChange = () => {
        if (dialog.open) {
          if (openTimer || openFired) return;
          openTimer = setTimeout(() => {
            openTimer = null;
            if (!dialog.open || openFired) return;
            openFired = true;
            proceeded = false;
            fire("multiupload_modal_open", {});
          }, OPEN_SETTLE_MS);
        } else {
          if (openTimer) {
            clearTimeout(openTimer);
            openTimer = null;
          }
          if (openFired && !proceeded) fire("multiupload_abandon", {});
          openFired = false;
          proceeded = false;
        }
      };
      new MutationObserver(onOpenChange).observe(dialog, {
        attributes: true,
        attributeFilter: ["open"]
      });
    }
  }

  // src/studio.js
  var root2;
  var host;
  var open = false;
  function initStudio(rootEl) {
    root2 = rootEl;
    host = root2.querySelector("[data-bsv4-studio-host]");
    if (!host) return;
    if (!window.__ensureV0Css) {
      window.__ensureV0Css = (cb) => {
        ensureStyles();
        if (typeof cb === "function") cb();
      };
    }
    document.addEventListener("bsv4:source-changed", (e) => {
      const src = e.detail && e.detail.source;
      if (src === "studio") openStudio();
      else if (open) closeStudio();
    });
  }
  function ensureStyles() {
    if (document.querySelector('link[rel="stylesheet"][href*="v0-styles"]')) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = host && host.dataset && host.dataset.bsv4V0css || "";
    if (!link.href) {
      const anyAsset = document.querySelector('link[href*="/assets/"], script[src*="/assets/"]');
      if (anyAsset) {
        const src = anyAsset.href || anyAsset.src;
        link.href = src.replace(/\/assets\/[^/?]+.*/, "/assets/v0-styles.css");
      }
    }
    if (link.href) document.head.appendChild(link);
  }
  function openStudio() {
    if (window.__ensureV0Js && !window.__v0JsLoaded) {
      window.__ensureV0Js(() => openStudio());
      return;
    }
    const editorOverlay = document.querySelector(".customTabelPopup__overlay-editor");
    const introOverlay = document.querySelector(".customTabelPopup__overlay-modal");
    if (!editorOverlay) return;
    ensureStyles();
    editorOverlay.__inlineParent = host;
    if (editorOverlay.parentNode !== host) host.appendChild(editorOverlay);
    editorOverlay.classList.add("studio-inline");
    editorOverlay.classList.remove("studio-embedded");
    editorOverlay.style.display = "block";
    if (introOverlay) introOverlay.style.display = "none";
    const swatch = document.querySelector(".transparent-swatch.canvas-color-swatch");
    if (swatch) {
      try {
        swatch.click();
      } catch (_) {
      }
    }
    const prevBtn = document.getElementById("prev-designs-btn");
    if (prevBtn && !prevBtn.__bsv4Wired) {
      prevBtn.__bsv4Wired = true;
      prevBtn.__spdInit = true;
      prevBtn.style.display = "";
      prevBtn.addEventListener("click", () => {
        window.__studioPickActive = true;
        window.__bsv4StudioPick = true;
        openPicker("previous");
      });
    }
    host.hidden = false;
    root2.querySelector(".bsv4-uploader__zone")?.setAttribute("data-bsv4-studio-hidden", "");
    open = true;
    if (window.matchMedia && window.matchMedia("(max-width: 749px)").matches) {
      window.__dtfMobileZoomTarget = 38;
      setZoomWhenReady(38);
    }
  }
  function closeStudio() {
    const editorOverlay = document.querySelector(".customTabelPopup__overlay-editor");
    if (editorOverlay) editorOverlay.style.display = "none";
    host.hidden = true;
    root2.querySelector(".bsv4-uploader__zone")?.removeAttribute("data-bsv4-studio-hidden");
    open = false;
  }
  function setZoomWhenReady(targetPct, tries = 0) {
    if (typeof window.setStudioZoom !== "function") {
      if (tries > 150) return;
      setTimeout(() => setZoomWhenReady(targetPct, tries + 1), 100);
      return;
    }
    try {
      window.setStudioZoom(targetPct);
    } catch (_) {
    }
    let attempts = 0;
    const iv = setInterval(() => {
      attempts++;
      const display = document.getElementById("zoom-display");
      const current = display && parseInt((display.textContent || "").replace("%", ""), 10);
      if (current && current !== targetPct) {
        try {
          window.setStudioZoom(targetPct);
        } catch (_) {
        }
      }
      if (attempts > 15) clearInterval(iv);
    }, 100);
  }

  // src/gallery.js
  function initGallery(root3) {
    const gal = root3.querySelector("[data-bsv4-gallery]");
    if (!gal) return;
    const slides = [...gal.querySelectorAll("[data-bsv4-slide]")];
    const thumbs = [...gal.querySelectorAll("[data-bsv4-thumb]")];
    let index = 0;
    const show = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle("is-active", n === index));
      thumbs.forEach((t, n) => t.classList.toggle("is-active", n === index));
      thumbs[index]?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      [slides[(index + 1) % slides.length], slides[(index - 1 + slides.length) % slides.length]].forEach((s) => {
        if (s && s.loading === "lazy") s.loading = "eager";
      });
    };
    gal.addEventListener("click", (e) => {
      const thumb = e.target.closest("[data-bsv4-thumb]");
      if (thumb) show(parseInt(thumb.dataset.bsv4Thumb, 10));
      else if (e.target.closest("[data-bsv4-gal-prev]")) show(index - 1);
      else if (e.target.closest("[data-bsv4-gal-next]")) show(index + 1);
    });
    const card = root3.querySelector("[data-bsv4-video-open]");
    const dialog = root3.querySelector("[data-bsv4-video-dialog]");
    if (card && dialog) {
      card.addEventListener("click", () => {
        dialog.showModal();
        dialog.querySelector("video")?.play().catch(() => {
        });
      });
      const close = () => {
        dialog.querySelector("video")?.pause();
        dialog.close();
      };
      dialog.querySelector("[data-bsv4-video-close]").addEventListener("click", close);
      dialog.addEventListener("click", (e) => {
        if (e.target === dialog) close();
      });
    }
  }

  // src/size-guide.js
  function containFit2(w, h, capW, capH) {
    w = parseFloat(w);
    h = parseFloat(h);
    capW = parseFloat(capW);
    capH = parseFloat(capH);
    if (!(w > 0) || !(h > 0) || !(capW > 0) || !(capH > 0)) return null;
    const scale = Math.min(capW / w, capH / h);
    return { w: round22(w * scale), h: round22(h * scale) };
  }
  function fitLabel(fit) {
    return fit ? `${fit.w}" x ${fit.h}"` : "";
  }
  function round22(n) {
    return Number(n.toFixed(2));
  }
  function designDims(file) {
    const rows = file.rows || [];
    const row = rows[rows.length - 1];
    const w = row && row.w != null ? row.w : file.widthIn != null ? file.widthIn : file.inW;
    const h = row && row.h != null ? row.h : file.heightIn != null ? file.heightIn : file.inH;
    return { w, h };
  }
  function initSizeGuide(root3, cfg7) {
    const block = root3.querySelector('[data-bsv4-block="size_guide"]');
    if (!block || !cfg7 || !cfg7.sizeGuide) return;
    if (block.dataset.bsv4SizeGuideInit === "1") return;
    block.dataset.bsv4SizeGuideInit = "1";
    const garments = cfg7.sizeGuide.garments || [];
    const tiles = Array.from(block.querySelectorAll("[data-bsv4-sg-tile]")).map((el, i) => {
      const g = garments[i] || {};
      return {
        el,
        capW: parseFloat(el.dataset.bsv4SgCapW != null ? el.dataset.bsv4SgCapW : g.capW),
        capH: parseFloat(el.dataset.bsv4SgCapH != null ? el.dataset.bsv4SgCapH : g.capH),
        label: el.querySelector("[data-bsv4-sg-label]"),
        design: el.querySelector("[data-bsv4-sg-design]")
      };
    });
    if (!tiles.length) return;
    for (const t of tiles) {
      if (!t.design) continue;
      t.design.addEventListener("load", () => {
        t.design.hidden = false;
      });
      t.design.addEventListener("error", () => {
        t.design.hidden = true;
      });
    }
    const custom = block.querySelector("[data-bsv4-sg-custom]");
    const customWrap = custom && custom.closest(".bsv4-size-guide__swatch--custom");
    function applyColor(color, isCustom) {
      for (const t of tiles) t.el.style.backgroundColor = color;
      if (customWrap) customWrap.classList.toggle("is-active", isCustom);
      if (isCustom) {
        for (const r of block.querySelectorAll("[data-bsv4-sg-swatch]")) r.checked = false;
      }
    }
    block.addEventListener("change", (e) => {
      const sw = e.target.closest && e.target.closest("[data-bsv4-sg-swatch]");
      if (sw && sw.checked) applyColor(sw.value, false);
    });
    if (custom) custom.addEventListener("input", () => applyColor(custom.value, true));
    subscribe((state2) => {
      const file = activeFile(state2);
      if (!file || !file.imgixUrl) {
        for (const t of tiles) {
          if (t.design) {
            t.design.hidden = true;
            t.design.removeAttribute("src");
          }
        }
        return;
      }
      const dims = designDims(file);
      const url = previewUrl(file);
      for (const t of tiles) {
        const fit = containFit2(dims.w, dims.h, t.capW, t.capH);
        if (!fit) continue;
        if (t.label) t.label.textContent = fitLabel(fit);
        if (t.design) {
          if (t.design.getAttribute("src") !== url) {
            t.design.hidden = true;
            t.design.src = url;
          }
        }
      }
    });
  }

  // src/main.js
  window.__bsv4 = { getState, update, subscribe, announce };
  window.__bsv4Bridge = {
    getState,
    update,
    subscribe,
    announce,
    uploadBlob,
    previewUrl,
    resolveDimensions,
    nanoBananaEdit,
    IMGIX_HOST,
    S3_HOST
  };
  function boot(root3) {
    if (!root3 || root3.dataset.bsv4Booted === "1") return;
    root3.dataset.bsv4Booted = "1";
    const cfg7 = readConfig();
    if (cfg7.family) update({ family: cfg7.family });
    if (cfg7.cartQty) update({ cartQty: Number(cfg7.cartQty) || 0 });
    subscribe((state2) => {
      root3.dataset.state = state2.status;
      root3.dataset.mode = state2.mode;
    });
    const safe = (name, fn) => {
      try {
        fn();
      } catch (err) {
        console.error(`bsv4: ${name} init failed`, err);
      }
    };
    safe("uploader", () => initUploader(root3, cfg7));
    safe("preview", () => initPreview(root3, cfg7));
    safe("modal", () => initModal(root3, cfg7));
    safe("sizing", () => initSizing(root3, cfg7));
    safe("pricing", () => initPricing(root3, cfg7));
    safe("cart", () => initCart(root3, cfg7));
    safe("designs", () => initDesigns(root3, cfg7));
    safe("addmore-popups", () => initAddmorePopups(root3));
    safe("bgr", () => initBgr(root3, cfg7));
    window.__bsv4Bridge.cfg = cfg7;
    const editSrc = root3.dataset.bsv4Editjs;
    if (window.__bsv4EditBoot) {
      safe("edit", () => window.__bsv4EditBoot());
    } else if (cfg7.falKey && editSrc && !window.__bsv4EditWired) {
      window.__bsv4EditWired = true;
      const fetchChunk = () => {
        const s = document.createElement("script");
        s.setAttribute("data-bsv4-editchunk", "");
        s.onerror = () => {
          s.remove();
          wireChunk();
        };
        s.src = editSrc;
        document.body.appendChild(s);
      };
      const wireChunk = () => {
        const un = subscribe((state2) => {
          if (!state2.files.length) return;
          un();
          fetchChunk();
        });
      };
      wireChunk();
    }
    safe("studio", () => initStudio(root3, cfg7));
    safe("gallery", () => initGallery(root3, cfg7));
    safe("size-guide", () => initSizeGuide(root3, cfg7));
    safe("integrations", () => initIntegrations(root3, cfg7));
    safe("ga-bridge", () => initGaBridge(root3));
    safe("handoffs", () => initHandoffs());
    announce("boot", { version: cfg7.version || "v5", family: cfg7.family || "dtf" });
  }
  boot(document.querySelector("[data-bsv4-root]"));
  document.addEventListener("shopify:section:load", (e) => {
    const root3 = e.target && e.target.querySelector && e.target.querySelector("[data-bsv4-root]");
    if (root3) boot(root3);
  });
})();


/* ── Price Calculator lazy-load wiring — verbatim from NT live bysize-v4.js boot (the 7-13
   sandbox this file was ported from predates the calculator). Clicking the collapsible
   section's Price Calculator link loads bysize-v4-calc.js (root's data-bsv4-calcjs), which
   boots the calculator popup from the cfg island's pricingTable. ── */
if (!window.__bsv4CalcWired) {
  window.__bsv4CalcWired = true;
  document.addEventListener(
    "click",
    (e) => {
      const trg = e.target.closest && e.target.closest("a.pricingTable, ._price-calc-link, .openPriceCalculator");
      if (!trg) return;
      const r = document.querySelector("[data-bsv4-root]");
      const calcSrc = r && r.dataset.bsv4Calcjs;
      if (!r || !calcSrc) return;
      e.preventDefault();
      e.stopPropagation();
      if (window.__bsv4CalcBoot) return void window.__bsv4CalcBoot(true);
      if (window.__bsv4CalcFetching) return;
      window.__bsv4CalcFetching = true;
      const s = document.createElement("script");
      s.onerror = () => {
        s.remove();
        window.__bsv4CalcFetching = false;
      };
      s.src = calcSrc;
      document.body.appendChild(s);
    },
    true
  );
}
